// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import {
  Animated,
  Modal,
  Pressable,
  ScrollView,
  Easing,
  Platform,
  Image,
  useWindowDimensions,
  PanResponder,
  View as NativeView,
} from 'react-native';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Redirect, useLocalSearchParams, useRouter, useRootNavigationState, usePathname } from 'expo-router';
import { useIsFocused, useNavigation } from '@react-navigation/native';

import { Text, View } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import { CoachMarkCallout } from '@/components/CoachMarkCallout';
import { t } from '@/i18n';
import {
  BffWebSocketClient,
  getSharedBffWebSocket,
  GameResultsEvent,
  HoleUpdateEvent,
  HoleInfoEvent,
  RoundStateEvent,
  TourLeaderboardStateEvent,
  TourLeaderboardUpdateEvent,
} from '@/services/bffWebSocket';
import { createAppLogger } from '@/services/appLogger';
import { setCurrentTourId } from '@/services/currentTour';
import { useRoundState } from '@/stores/roundState';
import { useDebugState } from '@/stores/debugState';
import { useDebugTraceState } from '@/stores/debugTraceState';
import { useUser } from '@/app/user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { getAssetImageSource } from '@/services/assetImageSource';
import { getActiveClaimSessionSync, getClaimClientIdSync, loadClaimSession } from '@/services/claimSession';
import { holeCompactScoreCoachMarkKey, useCoachMark } from '@/services/coachMarks';
import {
  GAME_ICON_MAP,
  BunkerIcon,
  BirdieImage,
  GolfballIcon,
  MysteryPrizeIcon,
  PenaltyIcon,
  ScorePopupLargeButtonImage,
  ScorePopupLongButtonImage,
  ScorePopupRailBannerImage,
  ThreePuttIcon,
  WaterIcon,
} from '@/features/hole/assets';

import {
  compactPlayerName,
  computeNetForHole,
  computeStrokesReceivedForHole,
  hazardIconForGameName,
  hazardTypeFromName,
  isUuidLike,
  normalizeGameSlug,
  normalizeHoleEntry,
  normalizeHoleEvent,
  normalizePlayerKey,
  normalizeTotalsList,
} from '@/features/hole/utils';
import type {
  DisplayTotal,
  GameDefinitionSummary,
  GameResultState,
  HoleEntry,
  HoleEventEntry,
  HoleMeta,
  Player,
  RoundSubgameEntry,
  ScoreTotal,
  Subgame,
  TeamSummary,
  TourLeaderboardResponse,
} from '@/features/hole/types';
import { getGameDisplayName, getSubgameDisplayName } from '@/features/game-definitions/i18n';
import {
  buildDisplayTotals,
  buildGameResultPlayers,
  buildGameResultRows,
  buildMysteryWinnersBySubgame,
  buildOrderedTeams,
  buildRoundPlacementByKey,
  buildTourLeaderKeys,
  buildTourPlacementByKey,
} from '@/features/hole/screen/selectors';
import { styles } from '@/features/hole/screen/styles';
import { useHoleActions } from '@/features/hole/screen/useHoleActions';
import { useHoleData } from '@/features/hole/screen/useHoleData';
import { useHoleRealtime } from '@/features/hole/screen/useHoleRealtime';
import type { HoleScreenViewModel } from '@/features/hole/screen/types';
import { HoleHeaderCard } from '@/features/hole/screen/components/HoleHeaderCard';
import appColors from '@/theme/colors';

// Cache game results per round so navigating between holes keeps the latest scores visible.
const gameResultsCache: Record<string, GameResultState> = {};
// Cache totals per round so leaderboard stays populated while moving between holes.
const roundTotalsCache: Record<string, ScoreTotal[]> = {};
// Cache leaderboard totals per round to avoid mixing with stroke totals when switching holes.
const roundLeaderboardCache: Record<string, ScoreTotal[]> = {};
// Show "next hole mystery prize" popup once per user+round+hole in current app runtime.
const seenNextMysteryPopupByUserRound = new Set<string>();
const HoleMarkImage = getAssetImageSource('images/hole_mark.png', 'icon', '/round/[roundId]/hole/[hole]');
const PegImage = getAssetImageSource('images/peg.png', '', '/round/[roundId]/hole/[hole]', { width: 80, height: 80, bucketStep: 1 });
const MAX_SWIPE_SCORE = 20;

const HOLE_MENU_COUNT = 18;
const HAZARD_GAME_META: Record<'water' | 'penalty' | 'bunker' | 'threePutt', { gameId: string; gameName: string }> = {
  water: { gameId: 'e0b7c8af-3dc8-4a9f-8b77-5d99e5da0a4b', gameName: 'Water' },
  penalty: { gameId: '6b9c72e0-6c1c-4d17-9f1f-6c0e2f71a742', gameName: 'Penalty' },
  bunker: { gameId: '4d3a1a42-4f67-41a4-b63d-9a9b50a83f1f', gameName: 'Bunker' },
  threePutt: { gameId: 'f9d88a1b-1a7e-4c86-9b2e-8e7f7f0fcb2b', gameName: '3 Putts' },
};

function mergeHoleEntriesForHole(
  existingEntries: HoleEntry[],
  targetHoleNumber: number,
  replacementEntries: HoleEntry[]
): HoleEntry[] {
  const next = existingEntries.filter((entry) => entry.holeNumber !== targetHoleNumber);
  replacementEntries.forEach((entry) => next.push(entry));
  return next;
}

function extractRoundGameDefinitionIdsFromLeaderboardState(
  message: TourLeaderboardStateEvent,
  roundId: string
): string[] {
  const normalizedRoundId = roundId.trim().toLowerCase();
  const result = new Set<string>();

  const rounds = (message.rounds ?? []) as Array<Record<string, unknown>>;
  rounds.forEach((round) => {
    const id = String(round.id ?? round.Id ?? '').trim().toLowerCase();
    if (!id || id !== normalizedRoundId) return;
    const ids = (round.gameDefinitionIds ?? round.GameDefinitionIds ?? []) as unknown[];
    ids.forEach((value) => {
      if (typeof value !== 'string') return;
      const trimmed = value.trim();
      if (isUuidLike(trimmed)) {
        result.add(trimmed);
      }
    });
  });

  const games = (message.games ?? []) as Array<Record<string, unknown>>;
  games.forEach((game) => {
    const gameRoundId = String(game.roundId ?? game.RoundId ?? '').trim().toLowerCase();
    if (gameRoundId && gameRoundId !== normalizedRoundId) return;
    const candidates = [game.gameDefinitionId, game.GameDefinitionId];
    candidates.forEach((candidate) => {
      if (typeof candidate !== 'string') return;
      const trimmed = candidate.trim();
      if (isUuidLike(trimmed)) {
        result.add(trimmed);
      }
    });
  });

  const scoresByRoundGame = (message.scoresByRoundGame ?? []) as Array<Record<string, unknown>>;
  scoresByRoundGame.forEach((row) => {
    const rowRoundId = String(row.roundId ?? row.RoundId ?? '').trim().toLowerCase();
    if (!rowRoundId || rowRoundId !== normalizedRoundId) return;
    const candidates = [row.gameDefinitionId, row.GameDefinitionId];
    candidates.forEach((candidate) => {
      if (typeof candidate !== 'string') return;
      const trimmed = candidate.trim();
      if (isUuidLike(trimmed)) {
        result.add(trimmed);
      }
    });
  });

  return Array.from(result);
}

export default function HoleScreen() {
  const {
    debug: debugParam,
    roundId: roundIdParam,
    hole: holeParam,
    gameDefinitionIds: gameDefinitionIdsParam,
    dir,
  } = useLocalSearchParams<{
    debug?: string;
    roundId?: string;
    hole?: string;
    gameDefinitionIds?: string;
    dir?: string;
  }>();
  const router = useRouter();
  const pathname = usePathname();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const isFocused = useIsFocused();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { height: windowHeight, width: windowWidth } = useWindowDimensions();
  const { user } = useUser();
  const [claimSession, setClaimSession] = useState(() => getActiveClaimSessionSync());
  const [claimSessionLoaded, setClaimSessionLoaded] = useState(false);
  const safePathname = pathname ?? '';
  const claimClientId = claimSession?.claimClientId ?? getClaimClientIdSync();
  const claimPlayerId = claimSession?.sourcePlayerId ?? null;
  const isGuestClaimMode = !user?.token && !!claimSession && !!claimClientId;
  const effectiveClaimClientId = isGuestClaimMode ? claimClientId : null;
  const isAnonymousReadOnlyMode = !user?.token;
  const shouldDeferAnonymousBootstrap = !user?.token && !claimSessionLoaded;
  const storedRoundId = useRoundState((state) => state.roundId);
  const storedHole = useRoundState((state) => state.hole);
  const holeMenuRequestVersion = useRoundState((state) => state.holeMenuRequestVersion);
  const clearHoleMenuRequest = useRoundState((state) => state.clearHoleMenuRequest);
  const setRoundId = useRoundState((state) => state.setRoundId);
  const setHole = useRoundState((state) => state.setHole);
  const roundIdFromParams = useMemo(() => {
    const raw = Array.isArray(roundIdParam) ? roundIdParam[0] : roundIdParam;
    return raw ?? null;
  }, [roundIdParam]);
  const roundIdFromPath = useMemo(() => {
    if (!safePathname) return null;
    const parts = safePathname.split('/').filter(Boolean);
    const roundIndex = parts.indexOf('round');
    if (roundIndex < 0) return null;
    return parts[roundIndex + 1] ?? null;
  }, [safePathname]);
  const holeFromParams = useMemo(() => {
    const raw = Array.isArray(holeParam) ? holeParam[0] : holeParam;
    if (!raw) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [holeParam]);
  const holeFromPath = useMemo(() => {
    if (!safePathname) return null;
    const parts = safePathname.split('/').filter(Boolean);
    const holeIndex = parts.indexOf('hole');
    if (holeIndex < 0) return null;
    const raw = parts[holeIndex + 1];
    if (!raw) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [safePathname]);
  const debug = useMemo(() => {
    const raw = Array.isArray(debugParam) ? debugParam[0] : debugParam;
    return raw === 'true' || raw === '1';
  }, [debugParam]);
  const isSmallScorePopupScreen = windowHeight <= 820;
  const shouldTopAlignScorePopup = isSmallScorePopupScreen || isGuestClaimMode;
  const setDebugEnabled = useDebugState((state) => state.setEnabled);
  const holeViewTraceId = useDebugTraceState((state) => state.holeViewTraceId);
  const setHoleViewTraceId = useDebugTraceState((state) => state.setHoleViewTraceId);
  const setHoleScoreTraceId = useDebugTraceState((state) => state.setHoleScoreTraceId);
  const gameDefinitionIdsFromParams = useMemo(() => {
    const rawValue = Array.isArray(gameDefinitionIdsParam) ? gameDefinitionIdsParam[0] : gameDefinitionIdsParam;
    const raw = typeof rawValue === 'string' ? rawValue : '';
    if (!raw) return [] as string[];
    return raw
      .split(',')
      .map((value: string) => value.trim())
      .filter((value: string) => isUuidLike(value));
  }, [gameDefinitionIdsParam]);
  const [tourId, setTourId] = useState<string | undefined>(undefined);
  const roundId = roundIdFromParams ?? roundIdFromPath ?? storedRoundId;
  const holeNumber = holeFromParams ?? holeFromPath ?? storedHole ?? 1;
  const currentHoleDataKey = roundId ? `${roundId}:${holeNumber}` : null;
  const compactScoreButtonCoachMarkRef = useRef<NativeView>(null);
  const [compactScoreCoachMarkDismissed, setCompactScoreCoachMarkDismissed] = useState(false);
  const [players, setPlayers] = useState<Player[]>([]);
  const { visible: showCompactScoreCoachMark } = useCoachMark(
    apiBaseUrl,
    holeCompactScoreCoachMarkKey,
    isFocused && !compactScoreCoachMarkDismissed && players.length > 0
  );
  const [scores, setScores] = useState<Record<string, string>>({});
  const [explicitScoreSelections, setExplicitScoreSelections] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totals, setTotals] = useState<ScoreTotal[]>([]);
  const [leaderboardTotals, setLeaderboardTotals] = useState<ScoreTotal[]>([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [leaderboardGamePoints, setLeaderboardGamePoints] = useState<Record<string, Array<{ gameId?: string; roundGamePoints: number; position?: number }>>>(
    {}
  );
  const [tourTotals, setTourTotals] = useState<Record<string, number>>({});
  const [teams, setTeams] = useState<TeamSummary[]>([]);
  const [gameDefinitionTypes, setGameDefinitionTypes] = useState<Record<string, string>>({});
  const [gameDefinitionNames, setGameDefinitionNames] = useState<GameDefinitionSummary[]>([]);
  const [selectedGameDefinitionIds, setSelectedGameDefinitionIds] = useState<string[]>(gameDefinitionIdsFromParams);
  const [holesCount, setHolesCount] = useState(18);
  const [holeEntries, setHoleEntries] = useState<HoleEntry[]>([]);
  const [allHoleEntries, setAllHoleEntries] = useState<HoleEntry[]>([]);
  const [loggedInPlayerId, setLoggedInPlayerId] = useState<string | null>(null);
  const [holeMenuOpen, setHoleMenuOpen] = useState(false);
  const [socketClient, setSocketClient] = useState<BffWebSocketClient | null>(null);
  const [gameResults, setGameResults] = useState<GameResultState>({});
  const [strokeIndex, setStrokeIndex] = useState<number | null>(null);
  const [holeMeta, setHoleMeta] = useState<Record<number, HoleMeta>>({});
  const [holePar, setHolePar] = useState<number | null>(null);
  const hasSelectedGames = selectedGameDefinitionIds.length > 0;

  useEffect(() => {
    setDebugEnabled(debug);
  }, [debug, setDebugEnabled]);

  useEffect(() => {
    if (!roundId) return;
    setHoleViewTraceId(generateTraceId());
  }, [roundId, holeNumber, setHoleViewTraceId]);
  const pendingAutoAdvanceHoleKeyRef = useRef<string | null>(null);
  const roundIdRef = useRef(roundId);
  const lastSyncedRoundIdRef = useRef<string | null>(null);
  const lastSyncedHoleRef = useRef<number | null>(null);
  const tourIdRef = useRef(tourId);
  const holeNumberRef = useRef(holeNumber);
  const holesCountRef = useRef(holesCount);
  const debugRef = useRef(false);
  const [isClient, setIsClient] = useState(Platform.OS !== 'web');
  const [showHoleDetails, setShowHoleDetails] = useState(false);
  const [eventCounts, setEventCounts] = useState<Record<string, { water: number; penalty: number; bunker: number; threePutt: number }>>({});
  const hazardOptimisticLockUntilRef = useRef<Record<string, number>>({});
  const [roundSubgames, setRoundSubgames] = useState<RoundSubgameEntry[]>([]);
  const [subgames, setSubgames] = useState<Subgame[]>([]);
  const [mysteryModalOpen, setMysteryModalOpen] = useState(false);
  const [nextMysteryModalOpen, setNextMysteryModalOpen] = useState(false);
  const [nextMysteryModalRows, setNextMysteryModalRows] = useState<Array<{ subgame: string; player: string }>>([]);
  const [holeEvents, setHoleEvents] = useState<HoleEventEntry[]>([]);
  const [scorePopupPlayerId, setScorePopupPlayerId] = useState<string | null>(null);
  const [scorePopupEightPlusOpen, setScorePopupEightPlusOpen] = useState(false);
  const [isScoreSwipeActive, setIsScoreSwipeActive] = useState(false);
  const rowScoreSwipeRef = useRef<
    Record<
      string,
      { startScore: number; lastStep: number; lastScoreValue: string | null; direction: -1 | 0 | 1; extremeDx: number }
    >
  >({});
  const scorePopupSheetTranslateY = useRef(new Animated.Value(420)).current;
  const [activeHoleDataKey, setActiveHoleDataKey] = useState<string | null>(null);
  const hasExactHoleData = !!currentHoleDataKey && activeHoleDataKey === currentHoleDataKey;

  useEffect(() => {
    let cancelled = false;
    loadClaimSession()
      .catch(() => null)
      .finally(() => {
        if (!cancelled) {
          setClaimSession(getActiveClaimSessionSync());
          setClaimSessionLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (tourId) {
      setCurrentTourId(tourId, { preserveRound: true });
    }
  }, [tourId]);

  useEffect(() => {
    const nextRoundId = roundIdFromParams ?? roundIdFromPath;
    if (nextRoundId && nextRoundId !== lastSyncedRoundIdRef.current) {
      lastSyncedRoundIdRef.current = nextRoundId;
      if (nextRoundId !== storedRoundId) {
        setRoundId(nextRoundId);
      }
    }
  }, [roundIdFromParams, roundIdFromPath, setRoundId, storedRoundId]);

  useEffect(() => {
    const nextHole = holeFromParams ?? holeFromPath;
    if (nextHole && nextHole !== lastSyncedHoleRef.current) {
      lastSyncedHoleRef.current = nextHole;
      if (nextHole !== storedHole) {
        setHole(nextHole);
      }
    }
  }, [holeFromParams, holeFromPath, setHole, storedHole]);

  useEffect(() => {
    if (roundId && storedHole == null && !holeFromParams && !holeFromPath) {
      setHole(1);
    }
  }, [holeFromParams, holeFromPath, roundId, setHole, storedHole]);

  useEffect(() => {
    if (!scorePopupPlayerId) {
      setScorePopupEightPlusOpen(false);
    }
  }, [scorePopupPlayerId]);

  useEffect(() => {
    if (!scorePopupPlayerId) return;
    scorePopupSheetTranslateY.setValue(420);
    Animated.timing(scorePopupSheetTranslateY, {
      toValue: 0,
      duration: 220,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [scorePopupPlayerId, scorePopupSheetTranslateY]);

  const closeScorePopup = useCallback(() => {
    if (!scorePopupPlayerId) return;
    setScorePopupEightPlusOpen(false);
    Animated.timing(scorePopupSheetTranslateY, {
      toValue: 420,
      duration: 180,
      easing: Easing.in(Easing.cubic),
      useNativeDriver: true,
    }).start(() => {
      setScorePopupPlayerId(null);
    });
  }, [scorePopupPlayerId, scorePopupSheetTranslateY]);

  useHoleRealtime({
    apiBaseUrl,
    userToken: user?.token,
    claimClientId: effectiveClaimClientId,
    allowAnonymous: isAnonymousReadOnlyMode,
    setSocketClient,
  });

  useEffect(() => {
    if (!isNavigationReady || !isFocused) return;
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setSubscriptionContext({ roundId });
  }, [apiBaseUrl, isFocused, isNavigationReady, roundId]);

  useEffect(() => {
    setNextMysteryModalOpen(false);
    setNextMysteryModalRows([]);
  }, [holeNumber]);

  useEffect(() => {
    roundIdRef.current = roundId;
    tourIdRef.current = tourId;
    holeNumberRef.current = holeNumber;
    holesCountRef.current = holesCount;
    debugRef.current = debug;
  }, [debug, holeNumber, holesCount, roundId, tourId]);

  useEffect(() => {
    if (!tourId || !user?.token) return;
    const loadTourTotals = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/tours/${tourId}/leaderboard`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) return;
        const payload = (await response.json()) as TourLeaderboardResponse & {
          Totals?: Array<{ PlayerId?: string; TotalPoints?: number }>;
        };
        const normalized =
          payload.totals ??
          payload.Totals?.map((t) => ({
            playerId: t.PlayerId ?? '',
            totalPoints: t.TotalPoints ?? 0,
          })) ??
          [];
        const map: Record<string, number> = {};
        normalized.forEach((t) => {
          if (!t.playerId) return;
          map[t.playerId] = t.totalPoints ?? 0;
          const match = playersRef.current.find((p) => p.id === t.playerId);
          if (match?.name) map[match.name] = t.totalPoints ?? 0;
        });
        setTourTotals(map);
      } catch {
        // ignore
      }
    };
    loadTourTotals();
  }, [apiBaseUrl, tourId, user?.token]);
  useEffect(() => {
    if (!roundId || !isNavigationReady) return;
    if (shouldDeferAnonymousBootstrap) return;
    if (isGuestClaimMode) {
      setRoundSubgames([]);
      return;
    }
    if (!user?.token) {
      setRoundSubgames([]);
      return;
    }
    const fetchSubgames = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}/subgames`, {
          headers: user?.token ? { Authorization: `Bearer ${user.token}` } : undefined,
        });
        if (!response.ok) {
          return;
        }
        const raw = (await response.json()) as Array<Record<string, unknown>>;
        const normalized = (raw ?? [])
          .map((item) => {
            const playerId = String(item.playerId ?? item.PlayerId ?? '');
            const subgameId = String(item.subgameId ?? item.SubgameId ?? '');
            const holeRaw = item.holeNumber ?? item.HoleNumber;
            const holeNumber = typeof holeRaw === 'number' ? holeRaw : Number(holeRaw);
            if (!playerId || !subgameId || !Number.isFinite(holeNumber)) {
              return null;
            }
            return {
              playerId,
              subgameId,
              holeNumber,
            } as RoundSubgameEntry;
          })
          .filter((entry): entry is RoundSubgameEntry => entry !== null);
        setRoundSubgames(normalized);
      } catch {
        setRoundSubgames([]);
      }
    };
    fetchSubgames();
  }, [apiBaseUrl, isNavigationReady, isGuestClaimMode, roundId, shouldDeferAnonymousBootstrap, user?.token]);
  useEffect(() => {
    if (!roundId || !isNavigationReady) return;
    if (shouldDeferAnonymousBootstrap) return;
    if (isGuestClaimMode) {
      setHoleEvents([]);
      return;
    }
    if (!user?.token) {
      setHoleEvents([]);
      return;
    }
    const fetchHoleEvents = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}/hole-events`, {
          headers: user?.token ? { Authorization: `Bearer ${user.token}` } : undefined,
        });
        if (!response.ok) {
          return;
        }
        const raw = (await response.json()) as Array<Record<string, unknown>>;
        const normalized = raw
          .map((item) => normalizeHoleEvent(item))
          .filter((item): item is HoleEventEntry => item !== null);
        setHoleEvents(normalized);
      } catch {
        setHoleEvents([]);
      }
    };
    fetchHoleEvents();
  }, [apiBaseUrl, isNavigationReady, isGuestClaimMode, roundId, shouldDeferAnonymousBootstrap, user?.token]);
  useEffect(() => {
    if (!isNavigationReady) return;
    if (shouldDeferAnonymousBootstrap) return;
    if (isGuestClaimMode) {
      setSubgames([]);
      return;
    }
    if (!user?.token) {
      setSubgames([]);
      return;
    }
    const fetchSubgameDefinitions = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/subgames`, {
          headers: user?.token ? { Authorization: `Bearer ${user.token}` } : undefined,
        });
        if (!response.ok) {
          return;
        }
        const data = (await response.json()) as Subgame[];
        setSubgames(data ?? []);
      } catch {
        setSubgames([]);
      }
    };
    fetchSubgameDefinitions();
  }, [apiBaseUrl, isNavigationReady, isGuestClaimMode, shouldDeferAnonymousBootstrap, user?.token]);
  const [birdsVisible, setBirdsVisible] = useState(false);
  const birdieScale = useRef(new Animated.Value(0.05)).current;
  const birdieRotate = useRef(new Animated.Value(0)).current;
  const birdieOpacity = useRef(new Animated.Value(1)).current;
  const hasSyncedRoundRef = useRef(false);
  const lastHoleViewKeyRef = useRef<string | null>(null);
  const lastHoleViewSentAtRef = useRef<number>(0);
  const socketConnectionEpochRef = useRef(0);
  const lastHoleViewEpochRef = useRef(-1);
  const playersRef = useRef<Player[]>([]);
  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        roundId,
        holeNumber,
        tourId,
        source: 'hole-ui',
        traceId: holeViewTraceId ?? undefined,
      }),
    [apiBaseUrl, holeNumber, holeViewTraceId, roundId, tourId, user?.token]
  );

  useEffect(() => {
    playersRef.current = players;
  }, [players]);
  useEffect(() => {
    if (Platform.OS === 'web') {
      setIsClient(true);
    }
  }, []);
  useEffect(() => {
    if (birdsVisible) {
      birdieScale.setValue(0.05);
      birdieRotate.setValue(0);
      birdieOpacity.setValue(1);
    }
  }, [birdsVisible, birdieOpacity, birdieRotate, birdieScale]);
  useEffect(() => {
    if (!isNavigationReady) return;
  }, [isNavigationReady]);

  const navigation = useNavigation();
  useLayoutEffect(() => {
    if (dir === '-1') {
      navigation.setOptions({ animation: 'slide_from_left' });
    } else {
      navigation.setOptions({ animation: 'slide_from_right' });
    }
  }, [dir, navigation]);

  useLayoutEffect(() => {
    setExplicitScoreSelections({});
    setActiveHoleDataKey(null);
  }, [holeNumber, roundId]);

  useEffect(() => {
    if (roundId && gameResultsCache[roundId]) {
      setGameResults(gameResultsCache[roundId]);
    } else {
      setGameResults({});
    }
    if (roundId && roundTotalsCache[roundId]) {
      setTotals(roundTotalsCache[roundId]);
    } else {
      setTotals([]);
    }
    if (roundId && roundLeaderboardCache[roundId]) {
      const cached = roundLeaderboardCache[roundId];
      setLeaderboardTotals(cached);
      const gamePointsMap: Record<string, Array<{ gameId?: string; roundGamePoints: number; position?: number }>> = {};
      cached.forEach((total) => {
        if (!total.playerId || !total.roundGamePoints) return;
        gamePointsMap[total.playerId] = total.roundGamePoints;
      });
      setLeaderboardGamePoints(gamePointsMap);
      const tourPointsMap: Record<string, number> = {};
      cached.forEach((total) => {
        if (total.tourGamesPoints === undefined) return;
        if (total.playerId) {
          tourPointsMap[total.playerId] = total.tourGamesPoints;
        }
        if (total.playerName) {
          tourPointsMap[total.playerName] = total.tourGamesPoints;
        }
      });
      if (Object.keys(tourPointsMap).length > 0) {
        setTourTotals((prev) => ({ ...prev, ...tourPointsMap }));
      }
    } else {
      setLeaderboardTotals([]);
      setLeaderboardGamePoints({});
    }
  }, [roundId]);

  useEffect(() => {
    if (!apiBaseUrl || !user?.token) {
      setGameDefinitionTypes({});
      return;
    }
    let cancelled = false;
    const fetchDefinitions = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/game-definitions`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });
        if (!response.ok) return;
        const payload = (await response.json()) as GameDefinitionSummary[];
        if (cancelled) return;
        const next: Record<string, string> = {};
        payload.forEach((def) => {
          if (!def?.id) return;
          const type = def.competitionType?.toLowerCase();
          if (type) {
            next[def.id] = type;
          }
        });
        setGameDefinitionTypes(next);
        setGameDefinitionNames(payload);
      } catch {
        if (!cancelled) {
          setGameDefinitionTypes({});
          setGameDefinitionNames([]);
        }
      }
    };
    fetchDefinitions();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, user?.token]);
  useEffect(() => {
    if (gameDefinitionIdsFromParams.length > 0) {
      setSelectedGameDefinitionIds(gameDefinitionIdsFromParams);
    }
  }, [gameDefinitionIdsFromParams]);

  useEffect(() => {
    if (gameDefinitionNames.length === 0) return;
    const allowedIds = new Set(
      gameDefinitionNames
        .map((definition) => definition.id)
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
    );
    setSelectedGameDefinitionIds((prev) => {
      const filtered = prev.filter((id) => allowedIds.has(id));
      if (filtered.length === prev.length) {
        return prev;
      }
      return filtered;
    });
  }, [gameDefinitionNames]);

  useEffect(() => {
    if (!roundId || !isNavigationReady) {
      setSelectedGameDefinitionIds([]);
      return;
    }
    if (!user?.token) {
      if (!isGuestClaimMode && gameDefinitionIdsFromParams.length === 0) {
        setSelectedGameDefinitionIds([]);
      }
      return;
    }
    let cancelled = false;
    const fetchRound = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) return;
        const data = (await response.json()) as {
          gameDefinitionIds?: string[];
          GameDefinitionIds?: string[];
          holesCount?: number;
          HolesCount?: number;
        };
        if (cancelled) return;
        const ids = data.gameDefinitionIds ?? data.GameDefinitionIds ?? [];
        if (ids.length > 0 || gameDefinitionIdsFromParams.length === 0) {
          setSelectedGameDefinitionIds(ids);
        }
        const nextHoles = data.holesCount ?? data.HolesCount;
        if (nextHoles === 9 || nextHoles === 18) {
          setHolesCount(nextHoles);
        } else {
          setHolesCount(18);
        }
      } catch {
        if (!cancelled && gameDefinitionIdsFromParams.length === 0) {
          setSelectedGameDefinitionIds([]);
          setHolesCount(18);
        }
      }
    };
    fetchRound();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, gameDefinitionIdsFromParams.length, isGuestClaimMode, isNavigationReady, roundId, user?.token]);

  const applyHandicapsFromMap = useCallback((mapObj: Record<string, unknown>) => {
    const map = new Map<string, number>();
    Object.entries(mapObj ?? {}).forEach(([k, v]) => {
      const num = typeof v === 'number' ? v : typeof v === 'string' ? parseInt(v, 10) : undefined;
      if (!Number.isFinite(num as number)) return;
      map.set(k, num as number);
    });
    if (map.size === 0) return;
    setPlayers((prev) => {
      let changed = false;
      const next = prev.map((p) => {
        const h = map.get(p.id) ?? map.get(p.name);
        if (h !== undefined && h !== p.handicap) {
          changed = true;
          return { ...p, handicap: h };
        }
        return p;
      });
      map.forEach((hcp, key) => {
        if (!next.some((p) => p.id === key || p.name === key)) {
          next.push({ id: key, name: key, createdAt: '', handicap: hcp });
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, []);

  const applyHandicapsFromEntries = useCallback((entries: HoleEntry[]) => {
    const map: Record<string, number> = {};
    entries.forEach((e) => {
      if (e.playingHandicap !== null && e.playingHandicap !== undefined) {
        const key = e.playerId ?? e.playerName;
        if (key) {
          map[key] = e.playingHandicap;
        }
      }
    });
    if (Object.keys(map).length === 0) return;
    applyHandicapsFromMap(map);
  }, [applyHandicapsFromMap]);

  useEffect(() => {
    const meta = holeMeta[holeNumber];
    if (meta?.par !== undefined) {
      setHolePar(meta.par ?? null);
    }
    if (meta?.strokeIndex !== undefined) {
      setStrokeIndex(meta.strokeIndex ?? null);
    }
  }, [holeMeta, holeNumber]);

  useEffect(() => {
    if (!currentHoleDataKey) return;
    if (!allHoleEntries.length) return;
    const exactHoleEntries = allHoleEntries.filter((entry) => entry.holeNumber === holeNumber);
    if (exactHoleEntries.length === 0) return;
    setHoleEntries(exactHoleEntries);
    applyHandicapsFromEntries(exactHoleEntries);
    setActiveHoleDataKey(currentHoleDataKey);
  }, [allHoleEntries, applyHandicapsFromEntries, currentHoleDataKey, holeNumber]);

  useEffect(() => {
    if (!holeEntries.length) return;
    const mapped: Record<string, string> = {};
    holeEntries.forEach((e) => {
      const val = e.grossStrokes === null ? (e.pickup ? 'Pickup' : '') : String(e.grossStrokes);
      const key = e.playerId ?? e.playerName;
      if (key) {
        if (val) mapped[key] = val;
      }
      if (e.playerName) {
        if (val) mapped[e.playerName] = val;
      }
    });
    setScores(mapped);
  }, [holeEntries, holeNumber]);

  useEffect(() => {
    if (!holeEvents.length) return;
    const hydratedCounts: Record<string, { water: number; penalty: number; bunker: number; threePutt: number }> = {};
    holeEvents.forEach((event) => {
      if (event.holeNumber !== holeNumber) return;
      const hazardType = hazardTypeFromName(event.eventType);
      if (!hazardType) return;
      const playerIdNorm = normalizePlayerKey(event.playerId);
      const matchedPlayer = playersRef.current.find((player) => {
        const idNorm = normalizePlayerKey(player.id);
        const nameNorm = normalizePlayerKey(player.name);
        return playerIdNorm === idNorm || playerIdNorm === nameNorm;
      });
      const eventKeys = new Set<string>([
        `${event.playerId}:${holeNumber}`,
      ]);
      if (matchedPlayer?.id) {
        eventKeys.add(`${matchedPlayer.id}:${holeNumber}`);
      }
      if (matchedPlayer?.name) {
        eventKeys.add(`${matchedPlayer.name}:${holeNumber}`);
      }
      const firstExisting = Array.from(eventKeys)
        .map((key) => hydratedCounts[key])
        .find((value) => !!value);
      const current = firstExisting ?? { water: 0, penalty: 0, bunker: 0, threePutt: 0 };
      if (hazardType === 'threeputt') {
        current.threePutt += 1;
      } else if (hazardType === 'water') {
        current.water += 1;
      } else if (hazardType === 'bunker') {
        current.bunker += 1;
      } else if (hazardType === 'penalty') {
        current.penalty += 1;
      }
      eventKeys.forEach((key) => {
        hydratedCounts[key] = { ...current };
      });
    });
    if (Object.keys(hydratedCounts).length === 0) return;
    setEventCounts((prev) => {
      let changed = false;
      const next = { ...prev };
      const now = Date.now();
      Object.entries(hydratedCounts).forEach(([eventKey, counts]) => {
        const existing = next[eventKey];
        const lockUntil = hazardOptimisticLockUntilRef.current[eventKey] ?? 0;
        const shouldKeepOptimistic = !!existing && lockUntil > now;
        const candidate = shouldKeepOptimistic ? existing : counts;
        const differs =
          !existing ||
          existing.water !== candidate.water ||
          existing.penalty !== candidate.penalty ||
          existing.bunker !== candidate.bunker ||
          existing.threePutt !== candidate.threePutt;
        if (differs) {
          next[eventKey] = candidate;
          changed = true;
        }
      });
      Object.entries(hazardOptimisticLockUntilRef.current).forEach(([eventKey, lockUntil]) => {
        if (lockUntil <= now) {
          delete hazardOptimisticLockUntilRef.current[eventKey];
        }
      });
      return changed ? next : prev;
    });
  }, [holeEvents, holeNumber]);

  const roundIdIsValid = useMemo(
    () => !!roundId && roundId !== '00000000-0000-0000-0000-000000000000',
    [roundId]
  );
  const formatLogMessage = useCallback((event: string, payload: Record<string, unknown>) => {
    const detailKeys = [
      'roundId',
      'tourId',
      'hole',
      'incomingRound',
      'expectedRound',
      'gameId',
      'playerId',
      'playerKey',
      'points',
      'games',
      'results',
      'entries',
      'totals',
      'joined',
      'requested',
      'type',
      'par',
      'strokeIndex',
      'updatedHoleNumber',
      'updatedPlayerId',
    ];
    const detailParts = detailKeys
      .filter((key) => key in payload)
      .map((key) => `${key}=${String(payload[key])}`);
    const messageText = typeof payload.message === 'string' ? payload.message : '';
    const prefix = messageText ? `${event} - ${messageText}` : event;
    return detailParts.length > 0 ? `${prefix} | ${detailParts.join(' ')}` : prefix;
  }, []);
  const baseLogContext = useMemo(
    () => ({ screen: 'hole', roundId, tourId, hole: holeNumber }),
    [roundId, tourId, holeNumber]
  );
  const logWithContext = useCallback(
    (event: string, payload: Record<string, unknown>, level?: 'debug' | 'warn' | 'error') => {
      const merged = { ...baseLogContext, ...payload };
      logToBff(formatLogMessage(event, merged), merged, level);
    },
    [baseLogContext, formatLogMessage, logToBff]
  );
  const logWithContextRef = useRef(logWithContext);
  useEffect(() => {
    logWithContextRef.current = logWithContext;
  }, [logWithContext]);
  const { navigateToHole, finishRound } = useHoleActions({
    debug,
    holeNumber,
    roundId,
    canMutateRoundStatus: !!user?.token,
    router: {
      push: (path: string) => router.push(path as never),
    },
    selectedGameDefinitionIds,
    setHole,
    socketClient,
    logWithContext,
  });
  const scorePopupPlayer = useMemo(
    () => players.find((player) => player.id === scorePopupPlayerId) ?? null,
    [players, scorePopupPlayerId]
  );
  const hazardCountKeysForPlayer = useCallback((player: Player) => {
    const keys = new Set<string>();
    const addKey = (value?: string | null) => {
      const trimmed = value?.trim();
      if (!trimmed) return;
      keys.add(`${trimmed}:${holeNumber}`);
    };
    addKey(player.id);
    addKey(player.name);
    return Array.from(keys);
  }, [holeNumber]);
  const resolveHazardCountsForPlayer = useCallback((player: Player | null) => {
    const empty = { water: 0, penalty: 0, bunker: 0, threePutt: 0 };
    if (!player) return empty;
    let countsFromEvents = empty;
    let hasEventCounts = false;
    const keys = hazardCountKeysForPlayer(player);
    for (const key of keys) {
      const existing = eventCounts[key];
      if (existing) {
        countsFromEvents = existing;
        hasEventCounts = true;
        break;
      }
    }
    const keyNorms = new Set<string>();
    if (player.id) keyNorms.add(normalizePlayerKey(player.id));
    if (player.name) keyNorms.add(normalizePlayerKey(player.name));
    const readFromGame = (gameId: string) => {
      const game = gameResults[gameId];
      if (!game?.scorePoints) {
        return { value: 0, hasMatchingPlayer: false };
      }
      for (const [key, value] of Object.entries(game.scorePoints)) {
        if (keyNorms.has(normalizePlayerKey(key))) {
          const numericValue = typeof value === 'number' ? value : Number(value);
          return {
            value: Number.isFinite(numericValue) ? Math.max(0, numericValue) : 0,
            hasMatchingPlayer: true,
          };
        }
      }
      return { value: 0, hasMatchingPlayer: false };
    };
    const waterFromGame = readFromGame(HAZARD_GAME_META.water.gameId);
    const penaltyFromGame = readFromGame(HAZARD_GAME_META.penalty.gameId);
    const bunkerFromGame = readFromGame(HAZARD_GAME_META.bunker.gameId);
    const threePuttFromGame = readFromGame(HAZARD_GAME_META.threePutt.gameId);
    const hasGameBackedHazards =
      waterFromGame.hasMatchingPlayer ||
      penaltyFromGame.hasMatchingPlayer ||
      bunkerFromGame.hasMatchingPlayer ||
      threePuttFromGame.hasMatchingPlayer;
    const derived = {
      water: waterFromGame.value,
      penalty: penaltyFromGame.value,
      bunker: bunkerFromGame.value,
      threePutt: threePuttFromGame.value,
    };
    if (hasEventCounts) {
      return countsFromEvents;
    }
    if (hasGameBackedHazards) {
      return derived;
    }
    return empty;
  }, [eventCounts, gameResults, hazardCountKeysForPlayer]);
  const updateCompactHazardCount = useCallback(
    (player: Player, field: 'water' | 'penalty' | 'bunker' | 'threePutt', delta: number) => {
      let nextValue = 0;
      setEventCounts((prev) => {
        const next = { ...prev };
        const keys = hazardCountKeysForPlayer(player);
        const lockUntil = Date.now() + 1200;
        keys.forEach((key) => {
          hazardOptimisticLockUntilRef.current[key] = lockUntil;
        });
        const current = keys
          .map((key) => next[key])
          .find((value) => !!value) ?? { water: 0, penalty: 0, bunker: 0, threePutt: 0 };
        nextValue = Math.max(0, current[field] + delta);
        const updated = { ...current, [field]: nextValue };
        keys.forEach((key) => {
          next[key] = updated;
        });
        return next;
      });
      if (socketClient && roundIdIsValid && roundId) {
        const sent = socketClient.send({
          type: 'hole.event',
          roundId,
          playerId: player.id,
          holeNumber,
          eventType: field,
          eventValue: null,
          eventDelta: delta,
          timestamp: new Date().toISOString(),
        });
        if (!sent) {
          logWithContext(
            'hole.event.sendFailed',
            { message: t('hole.eventSendFailed'), roundId, hole: holeNumber, playerId: player.id, eventType: field },
            'warn'
          );
          return;
        }
        const hazardGame = HAZARD_GAME_META[field];
        if (hazardGame) {
          setGameResults((prev) => {
            const next = { ...prev };
            const existing = next[hazardGame.gameId] ?? { name: hazardGame.gameName, scorePoints: {} };
            const idKey = player.id;
            const nameKey = player.name;
            const currentValue =
              existing.scorePoints[idKey] ??
              existing.scorePoints[nameKey] ??
              0;
            const nextValue = currentValue + delta;
            existing.name = existing.name ?? hazardGame.gameName;
            existing.scorePoints = {
              ...existing.scorePoints,
              [idKey]: nextValue,
              [nameKey]: nextValue,
            };
            next[hazardGame.gameId] = existing;
            if (roundId) {
              gameResultsCache[roundId] = next;
            }
            return next;
          });
          setLeaderboardGamePoints((prev) => {
            const next = { ...prev };
            const existingList = [...(next[player.id] ?? [])];
            const idx = existingList.findIndex((entry) => (entry.gameId ?? '') === hazardGame.gameId);
            if (idx >= 0) {
              existingList[idx] = {
                ...existingList[idx],
                roundGamePoints: (existingList[idx].roundGamePoints ?? 0) + delta,
              };
            } else {
              existingList.push({
                gameId: hazardGame.gameId,
                roundGamePoints: delta,
              });
            }
            next[player.id] = existingList;
            return next;
          });
        }
      }
    },
    [hazardCountKeysForPlayer, logWithContext, roundId, roundIdIsValid, socketClient, holeNumber]
  );

  useEffect(() => {
    if (!socketClient || !roundIdIsValid || !roundId) return;
    const holeViewKey = `${roundId}:${holeNumber}`;
    const connectionEpoch = socketConnectionEpochRef.current;
    const shouldSend =
      lastHoleViewKeyRef.current !== holeViewKey || lastHoleViewEpochRef.current !== connectionEpoch;
    if (!shouldSend) return;
    lastHoleViewKeyRef.current = holeViewKey;
    lastHoleViewEpochRef.current = connectionEpoch;
    lastHoleViewSentAtRef.current = Date.now();
    socketClient.send({ type: 'hole.view', roundId, hole: holeNumber });
    logWithContextRef.current('hole.view.sent', { message: 'Sent hole.view request to BFF.' });
  }, [socketClient, roundIdIsValid, roundId, holeNumber]);

  useHoleData({
    apiBaseUrl,
    user: user ?? null,
    allowGuestClaimMode: isGuestClaimMode || shouldDeferAnonymousBootstrap,
    claimPlayerId,
    roundId,
    roundIdIsValid,
    isNavigationReady,
    setLoading,
    setError,
    setPlayers,
    setTourId,
    setTeams,
    setLoggedInPlayerId,
    setAllHoleEntries,
    logWithContext,
  });

  useEffect(() => {
    if (!isFocused || !isNavigationReady) return;
    if (!safePathname.includes('/hole/')) return;
    if (!holeMenuRequestVersion) return;
    setHoleMenuOpen((prev) => !prev);
    clearHoleMenuRequest();
  }, [clearHoleMenuRequest, holeMenuRequestVersion, isFocused, isNavigationReady, safePathname]);

  useEffect(() => {
    if (!roundId || !isNavigationReady) return;
    const client = getSharedBffWebSocket(apiBaseUrl);
    client.setClaimClientId(effectiveClaimClientId ?? null);
    client.setAnonymousReadOnly(isAnonymousReadOnlyMode);
    client.setClaimAccessToken(claimSession?.claimAccessToken ?? null);
    client.setAuthToken(user?.token ?? null);
    setSocketClient(client);
    
    const resubscribeRound = () => {
      const joined = client.send({ type: 'round.join', roundId });
      const joinedTour =
        isGuestClaimMode && !!claimSession?.tourId
          ? client.send({ type: 'tour.join', tourId: claimSession.tourId })
          : false;
      const requested = client.send({ type: 'round.requestState', roundId });
      const requestedTourLeaderboard =
        isGuestClaimMode && (claimSession?.tourId || claimSession?.inviteToken)
          ? client.sendTourLeaderboardRequest({
              tourId: claimSession?.tourId ?? undefined,
              inviteToken: claimSession.inviteToken ?? undefined,
              requestId: generateTraceId(),
            })
          : false;
      hasSyncedRoundRef.current = true;
      logWithContextRef.current('hole.round.resubscribe', {
        message: 'Requested round.join and round.requestState.',
        joined,
        joinedTour,
        requested,
        requestedTourLeaderboard,
      });
    };
    const unsubscribeMessages = client.onMessage((evt) => {
      const evtType = (evt as { type?: string }).type;
      logWithContext(
        `hole.ws.message.${evtType ?? 'unknown'}`,
        { message: 'Received websocket message.', type: evtType },
        'debug'
      );
      if ((evt as { type?: string }).type === 'round.state') {
        logWithContext('hole.ws.round.state.raw', { message: 'Raw round.state payload.' }, 'debug');
      }
      if ((evt as HoleUpdateEvent).type === 'hole.update' && (evt as HoleUpdateEvent).roundId === roundId) {
        const holeEvent = evt as HoleUpdateEvent;
        logWithContext(
          'hole.event.update',
          {
            message: 'Applied hole.update event.',
            hole: holeEvent.hole,
            totals: holeEvent.totals?.length ?? 0,
            entries: holeEvent.entries?.length ?? 0,
            strokeIndex: holeEvent.strokeIndex,
            par: holeEvent.par,
          },
          'debug'
        );
        const playingHandicapsMap = (holeEvent as unknown as { playingHandicaps?: Record<string, unknown>; handicaps?: Record<string, unknown> }).playingHandicaps ??
          (holeEvent as unknown as { handicaps?: Record<string, unknown> }).handicaps;
        if (playingHandicapsMap) {
          applyHandicapsFromMap(playingHandicapsMap as Record<string, unknown>);
        }
        if (holeEvent.entries) {
          const filtered = holeEvent.entries
            .map((e) =>
              normalizeHoleEntry({
                ...e,
                holeNumber: holeEvent.hole,
                strokeIndex: holeEvent.strokeIndex ?? null,
              } as Record<string, unknown>)
            );
          setHoleEntries(filtered);
          setAllHoleEntries((prev) => mergeHoleEntriesForHole(prev, holeEvent.hole, filtered));
          applyHandicapsFromEntries(filtered);
          if (roundId && holeEvent.hole === holeNumber) {
            setActiveHoleDataKey(`${roundId}:${holeNumber}`);
          }
          const firstIdx =
            holeEvent.strokeIndex ??
            filtered.find((e) => e.strokeIndex !== undefined && e.strokeIndex !== null)?.strokeIndex ??
            null;
          setStrokeIndex(firstIdx);
          setHoleMeta((prev) => {
            const current = prev[holeNumber] ?? {};
            const strokeIndex = firstIdx ?? current.strokeIndex;
            const par = holeEvent.par ?? current.par;
            return { ...prev, [holeNumber]: { ...current, strokeIndex, par } };
          });
          const mapped: Record<string, string> = {};
          filtered.forEach((e) => {
            const gross = e.grossStrokes ?? null;
            const val = gross === null ? (e.pickup ? 'Pickup' : '') : String(gross);
            const key = e.playerId ?? e.playerName;
            if (key && val) mapped[key] = val;
            if (e.playerName && val) mapped[e.playerName] = val;
          });
          setScores(mapped);
        }
        if (holeEvent.totals) {
          setTotals(normalizeTotalsList(holeEvent.totals as ScoreTotal[], playersRef.current));
        }
      } else if ((evt as TourLeaderboardStateEvent).type === 'tour.leaderboard.state') {
        const leaderboardState = evt as TourLeaderboardStateEvent;
        const incomingPlayers = (leaderboardState.players ?? []) as Array<Record<string, unknown>>;
        if (incomingPlayers.length > 0) {
          const mappedPlayers = incomingPlayers
            .map((player) => {
              const id = String(player.id ?? player.Id ?? '').trim();
              if (!id) return null;
              const name = String(player.name ?? player.Name ?? '').trim();
              return {
                id,
                name: name || id,
                createdAt: String(player.createdAt ?? player.CreatedAt ?? '').trim(),
              } satisfies Player;
            })
            .filter((player): player is Player => player !== null);
          if (mappedPlayers.length > 0) {
            setPlayers(mappedPlayers);
          }
        }
        const leaderboardGameIds = extractRoundGameDefinitionIdsFromLeaderboardState(leaderboardState, roundId);
        if (leaderboardGameIds.length > 0) {
          setSelectedGameDefinitionIds(leaderboardGameIds);
        }
      } else if ((evt as TourLeaderboardUpdateEvent).type === 'tour.leaderboard.update') {
        // Intentionally ignore score payloads here.
        // Hole page game results are driven by round/game events to keep claimed/logged-in parity.
      } else if ((evt as RoundStateEvent).type === 'round.state' && (evt as RoundStateEvent).roundId === roundId) {
        const roundState = evt as RoundStateEvent;
        if (Array.isArray(roundState.players) && roundState.players.length > 0) {
          setPlayers((prev) => {
            const mapped = roundState.players
              ?.map((player) => ({
                id: String(player.id ?? '').trim(),
                name: String(player.name ?? '').trim(),
                createdAt: String(player.createdAt ?? '').trim(),
              }))
              .filter((player) => !!player.id)
              .map((player) => ({
                ...player,
                name: player.name || player.id,
                joinedAt: player.createdAt || undefined,
              })) as Player[];
            if (mapped.length === 0) {
              return prev;
            }
            return mapped;
          });
        }
        const roundStateGameIds =
          ((roundState as unknown as { gameDefinitionIds?: string[]; GameDefinitionIds?: string[] }).gameDefinitionIds ??
            (roundState as unknown as { GameDefinitionIds?: string[] }).GameDefinitionIds ??
            [])
            .filter((id): id is string => typeof id === 'string' && isUuidLike(id));
        if (roundStateGameIds.length > 0) {
          setSelectedGameDefinitionIds(roundStateGameIds);
        }
        if (roundState.holes) {
          setHoleMeta((prev) => {
            const next = { ...prev };
            roundState.holes?.forEach((h) => {
              const prior = next[h.holeNumber] ?? {};
              const strokeIndex = h.strokeIndex ?? h.index ?? prior.strokeIndex;
              next[h.holeNumber] = {
                ...prior,
                par: h.par ?? prior.par,
                strokeIndex,
                name: h.name ?? prior.name,
                description: h.description ?? prior.description,
              };
            });
            return next;
          });
        }
        if (roundState.holeEntries) {
          const normalizedEntries = roundState.holeEntries
            .map((h) => normalizeHoleEntry(h as Record<string, unknown>))
            .filter((entry) => entry.holeNumber > 0);
          setAllHoleEntries(normalizedEntries);
          const filtered = normalizedEntries.filter((h) => h.holeNumber === holeNumber);
          setHoleEntries(filtered);
          applyHandicapsFromEntries(filtered);
          if (roundId && filtered.length > 0) {
            setActiveHoleDataKey(`${roundId}:${holeNumber}`);
          }
        }
        const rawHoleEvents =
          ((roundState as unknown as { holeEvents?: Array<Record<string, unknown>>; HoleEvents?: Array<Record<string, unknown>> }).holeEvents ??
            (roundState as unknown as { HoleEvents?: Array<Record<string, unknown>> }).HoleEvents);
        if (Array.isArray(rawHoleEvents)) {
          const normalizedHoleEvents = rawHoleEvents
            .map((event) => normalizeHoleEvent(event))
            .filter((event): event is HoleEventEntry => event !== null);
          setHoleEvents(normalizedHoleEvents);
        }
        if (roundState.totals) {
          const normalizedTotals = normalizeTotalsList(roundState.totals as ScoreTotal[], playersRef.current);
          setTotals(normalizedTotals);
          if (roundId) {
            roundTotalsCache[roundId] = normalizedTotals;
          }
        }
      } else if ((evt as HoleInfoEvent).type === 'hole.info') {
        const info = evt as HoleInfoEvent;
        logWithContext(
          'hole.event.info',
          {
            message: 'Received hole.info.',
            hole: info.hole,
            par: info.par,
            strokeIndex: info.strokeIndex,
            name: info.name,
            lengthMeters: info.lengthMeters,
          },
          'debug'
        );
        if (info.hole === holeNumber) {
          setHoleMeta((prev) => {
            const current = prev[holeNumber] ?? {};
            return {
              ...prev,
              [holeNumber]: {
                ...current,
                par: info.par ?? current.par,
                strokeIndex: info.strokeIndex ?? current.strokeIndex,
                name: info.name ?? current.name,
                description: info.description ?? current.description,
                lengthMeters: info.lengthMeters ?? current.lengthMeters,
              },
            };
          });
          setHolePar((info.par ?? null) ?? null);
          setStrokeIndex((info.strokeIndex ?? null) ?? null);
        }
      } else if (((evt as HoleUpdateEvent).type === 'hole.update' || (evt as { type?: string }).type === 'hole.updated') && (evt as HoleUpdateEvent).roundId === roundId) {
        const holeEvent = evt as HoleUpdateEvent;
        logWithContext(
          'hole.event.updated',
          {
            message: 'Received hole.updated or secondary hole.update.',
            hole: holeEvent.hole,
            totals: holeEvent.totals?.length ?? 0,
            entries: holeEvent.entries?.length ?? 0,
            par: holeEvent.par,
            strokeIndex: holeEvent.strokeIndex,
          },
          'debug'
        );
      const entries = holeEvent.entries?.map((e) => normalizeHoleEntry(e as Record<string, unknown>)) ?? [];
      setAllHoleEntries((prev) => mergeHoleEntriesForHole(prev, holeEvent.hole, entries));
      if (holeEvent.hole === holeNumber) {
        setHoleEntries(entries);
        applyHandicapsFromEntries(entries);
        if (roundId && entries.length > 0) {
          setActiveHoleDataKey(`${roundId}:${holeNumber}`);
        }
        if (holeEvent.par !== undefined && holeEvent.par !== null) setHolePar(holeEvent.par);
        if (holeEvent.strokeIndex !== undefined && holeEvent.strokeIndex !== null) setStrokeIndex(holeEvent.strokeIndex);
      }
      if (holeEvent.totals) {
        const normalizedTotals = normalizeTotalsList(holeEvent.totals as ScoreTotal[], playersRef.current);
        setTotals(normalizedTotals);
        if (roundId) {
          roundTotalsCache[roundId] = normalizedTotals;
        }
      }
      // Ask for the latest round state to backfill any missed results/holes.
      if (client && roundId) {
        client.send({ type: 'round.requestState', roundId });
      }
      } else if ((evt as { type?: string }).type === 'round.leaderboard' && (evt as { roundId?: string }).roundId === roundId) {
        const lbTotals = (evt as { totals?: ScoreTotal[] }).totals;
        if (lbTotals) {
          const normalizedTotals = normalizeTotalsList(lbTotals as ScoreTotal[], playersRef.current);
          setLeaderboardTotals(normalizedTotals);
          const gamePointsMap: Record<string, Array<{ gameId?: string; roundGamePoints: number; position?: number }>> = {};
          normalizedTotals.forEach((total) => {
            if (!total.roundGamePoints) return;
            if (total.playerId) {
              gamePointsMap[total.playerId] = total.roundGamePoints;
            }
            if (total.playerName) {
              gamePointsMap[total.playerName] = total.roundGamePoints;
            }
          });
          setLeaderboardGamePoints(gamePointsMap);
          const tourPointsMap: Record<string, number> = {};
          normalizedTotals.forEach((total) => {
            if (total.tourGamesPoints === undefined) return;
            if (total.playerId) {
              tourPointsMap[total.playerId] = total.tourGamesPoints;
            }
            if (total.playerName) {
              tourPointsMap[total.playerName] = total.tourGamesPoints;
            }
          });
          if (Object.keys(tourPointsMap).length > 0) {
            setTourTotals((prev) => ({ ...prev, ...tourPointsMap }));
          }
          if (roundId) {
            roundLeaderboardCache[roundId] = normalizedTotals;
          }
        }
        if (client && roundId) {
          client.send({ type: 'round.requestState', roundId });
        }
      } else if ((evt as { type?: string }).type === 'round.updated' && (evt as { roundId?: string }).roundId === roundId) {
        const updatedIds =
          (evt as { gameDefinitionIds?: string[]; GameDefinitionIds?: string[] }).gameDefinitionIds ??
          (evt as { GameDefinitionIds?: string[] }).GameDefinitionIds ??
          [];
        if (Array.isArray(updatedIds)) {
          const validIds = updatedIds.filter((id): id is string => typeof id === 'string' && isUuidLike(id));
          if (validIds.length > 0) {
            setSelectedGameDefinitionIds(validIds);
          }
        }
      } else if ((evt as { type?: string }).type === 'game.result') {
        const aggregatedEvent = evt as GameResultsEvent;
        const incomingRound = aggregatedEvent.roundId;
        const normalizedIncomingRound = String(incomingRound ?? '').trim().toLowerCase();
        const normalizedExpectedRound = String(roundId ?? '').trim().toLowerCase();
        const incomingGameId = aggregatedEvent.games?.[0]?.gameId ?? 'game';
        logWithContext(
          'hole.game.result.received',
          {
            message: 'Received game.result.',
            incomingRound,
            expectedRound: roundId,
            gameId: incomingGameId,
            updatedHoleNumber: aggregatedEvent.updatedHoleNumber,
            updatedPlayerId: aggregatedEvent.updatedPlayerId,
            games: aggregatedEvent.games?.length ?? 0,
          },
          'debug'
        );
        if (!normalizedIncomingRound || !normalizedExpectedRound || normalizedIncomingRound !== normalizedExpectedRound) {
          logWithContext(
            'hole.game.result.skipped',
            {
              message: 'Skipped game.result for other round.',
              incomingRound,
              expectedRound: roundId,
              gameId: incomingGameId,
            },
            'warn'
          );
          return;
        }
        const aggregated: GameResultState = {};
        aggregatedEvent.games.forEach((game) => {
          const gameId = game.gameId ?? 'game';
          const current = aggregated[gameId] ?? { name: game.gameName ?? 'Game', scorePoints: {} };
          (game.players ?? []).forEach((player) => {
            const matchingPlayer = playersRef.current.find((p) => p.id === player.playerId || p.name === player.playerId);
            const playerKey = matchingPlayer?.id ?? player.playerId;
            const playerNameKey = matchingPlayer?.name && matchingPlayer.name !== playerKey ? matchingPlayer.name : null;
            const normIdKey = normalizePlayerKey(playerKey);
            const normNameKey = normalizePlayerKey(playerNameKey);
            if (playerKey) current.scorePoints[playerKey] = player.lastScorePoints;
            if (playerNameKey) current.scorePoints[playerNameKey] = player.lastScorePoints;
            if (normIdKey) current.scorePoints[normIdKey] = player.lastScorePoints;
            if (normNameKey) current.scorePoints[normNameKey] = player.lastScorePoints;
          });
          current.name = current.name ?? game.gameName ?? 'Game';
          aggregated[gameId] = current;
        });
        setGameResults(aggregated);
        if (roundId) {
          gameResultsCache[roundId] = aggregated;
        }
        logWithContext(
          'hole.game.result.applied',
          {
            message: 'Applied aggregated game.result to local gameResults.',
            roundId,
            games: Object.keys(aggregated).length,
            gameIds: Object.keys(aggregated),
          },
          'debug'
        );
        if (client && roundId) {
          client.send({ type: 'round.requestState', roundId });
        }
      }
    });

    const unsubscribeConnection = client.onConnectionChange((state) => {
      if (state === 'connected') {
        setError(null);
        socketConnectionEpochRef.current += 1;
        hasSyncedRoundRef.current = false;
        if (!hasSyncedRoundRef.current) {
          resubscribeRound();
        }
        const currentRoundId = roundIdRef.current;
        const currentHole = holeNumberRef.current;
        if (currentRoundId) {
          const holeViewKey = `${currentRoundId}:${currentHole}`;
          const connectionEpoch = socketConnectionEpochRef.current;
          const shouldSendHoleView =
            lastHoleViewKeyRef.current !== holeViewKey || lastHoleViewEpochRef.current !== connectionEpoch;
          if (shouldSendHoleView) {
            lastHoleViewKeyRef.current = holeViewKey;
            lastHoleViewEpochRef.current = connectionEpoch;
            lastHoleViewSentAtRef.current = Date.now();
            client.send({ type: 'hole.view', roundId: currentRoundId, hole: currentHole });
            logWithContextRef.current('hole.view.sent', { message: 'Sent hole.view request to BFF after reconnect.' });
          }
        }
        if (isGuestClaimMode && (claimSession?.tourId || claimSession?.inviteToken)) {
          client.sendTourLeaderboardRequest({
            tourId: claimSession?.tourId ?? undefined,
            inviteToken: claimSession.inviteToken ?? undefined,
            requestId: generateTraceId(),
          });
        }
      } else {
        hasSyncedRoundRef.current = false;
      }
    });

    const unsubscribeErrors = client.onError((message) => {
      const normalized = String(message ?? '').trim().toLowerCase();
      if (normalized.includes('anonymous connections are read-only')) {
        return;
      }
      setError(message);
    });

    resubscribeRound();

    return () => {
      client.send({ type: 'round.leave', roundId });
      hasSyncedRoundRef.current = false;
      unsubscribeMessages();
      unsubscribeConnection();
      unsubscribeErrors();
      logWithContextRef.current('hole.round.leave', { message: 'Sent round.leave for cleanup.' }, 'debug');
    };
  }, [
    apiBaseUrl,
    effectiveClaimClientId,
    roundId,
    user?.token,
    isGuestClaimMode,
    isAnonymousReadOnlyMode,
    claimSession?.tourId,
    claimSession?.inviteToken,
    claimSession?.claimAccessToken,
    isNavigationReady,
    applyHandicapsFromEntries,
    applyHandicapsFromMap,
    holeNumber,
    logWithContext,
  ]);

  const handleScore = async (player: Player, value: string, options?: { suppressCelebration?: boolean; suppressAutoAdvance?: boolean }) => {
    if (!roundId) return;
    if (!socketClient) {
      setError('Ingen anslutning till BFF:en. Försök igen.');
      return;
    }
    const traceId = generateTraceId();
    setHoleScoreTraceId(traceId);
    const parToSend = holePar ?? currentHoleMeta.par ?? 4;
    if (!holePar && !currentHoleMeta.par) {
      logToBff('hole.birdie.par.missing', { holeNumber, parToSend, value }, 'warn');
    }
    const numericValue = Number.parseInt(value, 10);
    if (!Number.isNaN(numericValue)) {
      if (numericValue === parToSend - 1 && !options?.suppressCelebration) {
        triggerBirdies();
      }
      logToBff('hole.birdie.handleScore', { scorePopupPlayerId: scorePopupPlayerId ?? null, value }, 'info');
    }
    if (!options?.suppressAutoAdvance) {
      pendingAutoAdvanceHoleKeyRef.current = `${roundId}:${holeNumber}`;
    }
    if (currentHoleDataKey) {
      setActiveHoleDataKey(currentHoleDataKey);
    }
    setScores((prev) => ({ ...prev, [player.name]: value, [player.id]: value }));
    setExplicitScoreSelections((prev) => ({ ...prev, [player.name]: true, [player.id]: true }));

    try {
      const body =
        value === 'Pickup'
          ? {
              roundId,
              playerId: player.id,
              hole: holeNumber,
              par: parToSend,
              strokes: null,
              pickup: true,
              timestamp: new Date().toISOString(),
              traceId,
            }
          : {
              roundId,
              playerId: player.id,
              hole: holeNumber,
              par: parToSend,
              strokes: parseInt(value, 10),
              pickup: false,
              timestamp: new Date().toISOString(),
              traceId,
            };
      const sent = socketClient.sendScoreUpdate(body);
      if (!sent) {
        throw new Error('Kunde inte nå BFF via websocket.');
      }

      const popupSeenKey = `${user?.id ?? 'unknown'}:${roundId}:${holeNumber}:mystery-next-hole`;
      const nextHole = holeNumber + 1;
      if (nextHole <= holesCount) {
        const upcomingEntries = roundSubgames.filter((entry) => entry.holeNumber === nextHole);
        if (upcomingEntries.length > 0) {
          if (!seenNextMysteryPopupByUserRound.has(popupSeenKey)) {
            const rows = upcomingEntries
              .map((entry) => ({
                subgame: subgameNameById[entry.subgameId] ?? t('hole.mysteryPrizeUnknownSubgame'),
                player: playerNameById[entry.playerId] ?? t('hole.mysteryPrizeUnknownPlayer'),
              }))
              .sort((a, b) => {
                const bySubgame = a.subgame.localeCompare(b.subgame);
                if (bySubgame !== 0) return bySubgame;
                return a.player.localeCompare(b.player);
              });
            setNextMysteryModalRows(rows);
            setNextMysteryModalOpen(true);
            seenNextMysteryPopupByUserRound.add(popupSeenKey);
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunde inte spara resultatet.');
    }
  };

  const normalizeSwipeScoreValue = useCallback((value: string | null | undefined, fallback: number) => {
    if (!value || value === 'Pickup') return fallback;
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.max(1, Math.min(MAX_SWIPE_SCORE, parsed));
  }, []);

  const formatSwipeScoreValue = useCallback((numeric: number) => {
    const bounded = Math.max(1, Math.min(MAX_SWIPE_SCORE, numeric));
    return String(bounded);
  }, []);

  const applySwipeScoreValue = (
    player: Player,
    targetNumeric: number,
    options?: { suppressCelebration?: boolean; suppressAutoAdvance?: boolean }
  ) => {
    const bounded = Math.max(1, Math.min(MAX_SWIPE_SCORE, targetNumeric));
    const nextValue = formatSwipeScoreValue(bounded);
    const currentValue = hasExactHoleData ? (scores[player.id] ?? scores[player.name] ?? null) : null;
    const currentParsed = currentValue ? Number.parseInt(currentValue, 10) : Number.NaN;
    const currentNumeric = Number.isFinite(currentParsed)
      ? Math.max(1, Math.min(MAX_SWIPE_SCORE, currentParsed))
      : null;
    const alreadySelected = currentNumeric === bounded;
    if (alreadySelected) return;
    void handleScore(player, nextValue, {
      suppressCelebration: options?.suppressCelebration ?? false,
      suppressAutoAdvance: options?.suppressAutoAdvance ?? false,
    });
  };

  const triggerBirdies = () => {
    setBirdsVisible(true);
    birdieScale.setValue(0.05);
    birdieRotate.setValue(0);
    birdieOpacity.setValue(1);
    logToBff('hole.birdie.trigger', { holeNumber }, 'info');
    const baseSize = 140;
    const fillScale = windowWidth / baseSize;
    const blowScale = fillScale;

    const startAnimation = () => {
      Animated.sequence([
        Animated.parallel([
          Animated.timing(birdieScale, {
            toValue: fillScale,
            duration: 2300,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: false,
          }),
          Animated.timing(birdieRotate, {
            toValue: 1,
            duration: 2300,
            easing: Easing.linear,
            useNativeDriver: false,
          }),
        ]),
        Animated.delay(1000),
        Animated.parallel([
          Animated.timing(birdieScale, {
            toValue: blowScale,
            duration: 300,
            easing: Easing.out(Easing.quad),
            useNativeDriver: false,
          }),
          Animated.timing(birdieOpacity, {
            toValue: 0,
            duration: 300,
            easing: Easing.out(Easing.quad),
            useNativeDriver: false,
          }),
        ]),
      ]).start(() => {
        setBirdsVisible(false);
        birdieScale.setValue(0.05);
        birdieRotate.setValue(0);
        birdieOpacity.setValue(1);
      });
    };

    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(startAnimation);
    } else {
      setTimeout(startAnimation, 0);
    }
  };

  const currentHoleMeta = holeMeta[holeNumber] ?? {};
  const parLabel = holePar ?? currentHoleMeta.par ?? '–';
  const hcpLabel = strokeIndex ?? currentHoleMeta.strokeIndex ?? '–';
  const parForHole = holePar ?? currentHoleMeta.par ?? null;
  const scorePopupSelectedValue = scorePopupPlayer
    ? (hasExactHoleData ? (scores[scorePopupPlayer.id] ?? scores[scorePopupPlayer.name] ?? null) : null)
    : null;
  const scorePopupSelectedMarkIndex = useMemo(() => {
    if (!scorePopupSelectedValue || scorePopupSelectedValue === 'Pickup') return null;
    if (scorePopupSelectedValue === '9+' || scorePopupSelectedValue === '10+') return 7;
    const numeric = Number.parseInt(scorePopupSelectedValue, 10);
    if (Number.isNaN(numeric)) return null;
    if (numeric >= 8) return 7;
    if (numeric >= 1 && numeric <= 7) return numeric - 1;
    return null;
  }, [scorePopupSelectedValue]);
  const scorePopupParMarkIndex = useMemo(() => {
    if (parForHole == null) return null;
    if (parForHole >= 8) return 7;
    if (parForHole >= 1 && parForHole <= 7) return parForHole - 1;
    return null;
  }, [parForHole]);
  const scorePopupEventCounts = useMemo(() => {
    return resolveHazardCountsForPlayer(scorePopupPlayer);
  }, [resolveHazardCountsForPlayer, scorePopupPlayer]);
  const allPlayersScoredForCurrentHole = useMemo(() => {
    if (!hasExactHoleData) return false;
    if (players.length === 0) return false;
    return players.every((player) => {
      const existing = holeEntries.find((entry) => {
        if (entry.holeNumber !== holeNumber) return false;
        const entryName = (entry.playerName ?? '').trim().toLowerCase();
        const playerName = player.name.trim().toLowerCase();
        return entry.playerId === player.id || entryName === playerName || entry.playerName === player.id;
      });
      if (existing && (existing.grossStrokes !== null && existing.grossStrokes !== undefined)) {
        return true;
      }
      if (existing?.pickup) {
        return true;
      }
      const selectedScore = scores[player.id] ?? scores[player.name];
      if (!selectedScore) {
        return false;
      }
      if (selectedScore === 'Pickup') {
        const hasExplicitScore =
          explicitScoreSelections[player.id] === true ||
          explicitScoreSelections[player.name] === true;
        return hasExplicitScore || !!existing?.pickup;
      }
      return true;
    });
  }, [explicitScoreSelections, hasExactHoleData, holeEntries, holeNumber, players, scores]);
  useEffect(() => {
    if (!roundId) return;
    if (!allPlayersScoredForCurrentHole) return;
    if (holeNumber >= holesCount) return;
    const currentHoleKey = `${roundId}:${holeNumber}`;
    if (pendingAutoAdvanceHoleKeyRef.current !== currentHoleKey) return;
    pendingAutoAdvanceHoleKeyRef.current = null;
    const timeout = setTimeout(() => {
      navigateToHole(holeNumber + 1);
    }, 220);
    return () => clearTimeout(timeout);
  }, [allPlayersScoredForCurrentHole, holeNumber, holesCount, navigateToHole, roundId]);
  const strokeIndexForHole = strokeIndex ?? currentHoleMeta.strokeIndex ?? null;
  const hasMysteryPrizeForHole = useMemo(
    () => roundSubgames.some((entry) => entry.holeNumber === holeNumber),
    [roundSubgames, holeNumber]
  );
  const mysteryEntriesForHole = useMemo(
    () => roundSubgames.filter((entry) => entry.holeNumber === holeNumber),
    [roundSubgames, holeNumber]
  );
  const playerNameById = useMemo(() => {
    const map: Record<string, string> = {};
    players.forEach((player) => {
      map[player.id] = player.name;
    });
    return map;
  }, [players]);
  const subgameNameById = useMemo(() => {
    const map: Record<string, string> = {};
    subgames.forEach((subgame) => {
      map[subgame.id] = getSubgameDisplayName(subgame, t('hole.mysteryPrizeUnknownSubgame'));
    });
    return map;
  }, [subgames]);
  const selectedHazardTypes = useMemo(() => {
    const types = new Set<string>();
    if (selectedGameDefinitionIds.length > 0) {
      const selected = new Set(selectedGameDefinitionIds);
      gameDefinitionNames
        .filter((def) => def.id && selected.has(def.id))
        .forEach((def) => {
          const hazardType = hazardTypeFromName(def.name ?? (def as { Name?: string }).Name);
          if (hazardType) {
            types.add(hazardType);
          }
        });
    }
    Object.values(gameResults).forEach((game) => {
      const hazardType = hazardTypeFromName(game.name);
      if (hazardType) {
        types.add(hazardType);
      }
    });
    return types;
  }, [gameDefinitionNames, gameResults, selectedGameDefinitionIds]);
  const isStablefordGame = useMemo(() => {
    const selected = new Set(selectedGameDefinitionIds);
    const fromDefinitions = gameDefinitionNames.some((def) => {
      if (!def.id || (selected.size > 0 && !selected.has(def.id))) return false;
      const name = (def.name ?? (def as { Name?: string }).Name ?? '').toLowerCase();
      return name.includes('stableford');
    });
    if (fromDefinitions) return true;
    return Object.values(gameResults).some((game) => (game.name ?? '').toLowerCase().includes('stableford'));
  }, [gameDefinitionNames, gameResults, selectedGameDefinitionIds]);
  const showPenaltyEvent = useMemo(() => selectedHazardTypes.has('penalty'), [selectedHazardTypes]);
  const showWaterEvent = useMemo(() => selectedHazardTypes.has('water'), [selectedHazardTypes]);
  const showBunkerEvent = useMemo(() => selectedHazardTypes.has('bunker'), [selectedHazardTypes]);
  const showThreePuttEvent = useMemo(() => selectedHazardTypes.has('threeputt'), [selectedHazardTypes]);
  const mysteryWinnersBySubgame = useMemo(
    () =>
      buildMysteryWinnersBySubgame({
        holeEvents,
        holeNumber,
      }),
    [holeEvents, holeNumber]
  );
  const mysteryWinnerIdsForHole = useMemo(() => {
    return new Set(Object.values(mysteryWinnersBySubgame).map((entry) => entry.playerId));
  }, [mysteryWinnersBySubgame]);
  const mysteryPrizeText = useMemo(() => {
    const entries = mysteryEntriesForHole;
    if (entries.length === 0) {
      return t('hole.mysteryPrizeNone');
    }
    const parts = entries.map((entry) => {
      const subgameName = subgameNameById[entry.subgameId] ?? t('hole.mysteryPrizeUnknownSubgame');
      const playerName = playerNameById[entry.playerId] ?? t('hole.mysteryPrizeUnknownPlayer');
      const winnerId = mysteryWinnersBySubgame[entry.subgameId]?.playerId;
      if (winnerId) {
        const winnerName = playerNameById[winnerId] ?? t('hole.mysteryPrizeUnknownPlayer');
        return t('hole.mysteryPrizeEntryWinner', { subgame: subgameName, player: playerName, winner: winnerName });
      }
      return t('hole.mysteryPrizeEntry', { subgame: subgameName, player: playerName });
    });
    return `${t('hole.mysteryPrizeTitle')}: ${parts.join(', ')}`;
  }, [mysteryEntriesForHole, mysteryWinnersBySubgame, playerNameById, subgameNameById]);
  const handleMysteryWinnerSelect = useCallback(
    (subgameId: string, winnerId: string) => {
      if (!roundId) return;
      const entry = mysteryEntriesForHole.find((item) => item.subgameId === subgameId);
      if (!entry) return;
      const currentWinnerId = mysteryWinnersBySubgame[subgameId]?.playerId ?? null;
      const isDeselect = currentWinnerId === winnerId;
      const socket = socketClient ?? getSharedBffWebSocket(apiBaseUrl);
      socket.setClaimClientId(effectiveClaimClientId ?? null);
      socket.setAnonymousReadOnly(isAnonymousReadOnlyMode);
      socket.setClaimAccessToken(claimSession?.claimAccessToken ?? null);
      socket.setAuthToken(user?.token ?? null);
      const timestamp = new Date().toISOString();
      const sent = socket.send({
        type: 'hole.event',
        roundId,
        playerId: winnerId,
        holeNumber: entry.holeNumber,
        eventType: 'mysteryWinner',
        eventValue: subgameId,
        eventDelta: isDeselect ? -1 : 1,
        timestamp,
      });
      if (!sent) {
        setError('Kunde inte spara mystery prize-vinnare.');
        return;
      }
      socket.send({ type: 'round.requestState', roundId });
      setHoleEvents((prev) => {
        if (isDeselect) {
          for (let index = prev.length - 1; index >= 0; index -= 1) {
            const candidate = prev[index];
            if (
              candidate.playerId === winnerId &&
              candidate.holeNumber === entry.holeNumber &&
              candidate.eventType === 'mysteryWinner' &&
              candidate.eventValue === subgameId
            ) {
              return [...prev.slice(0, index), ...prev.slice(index + 1)];
            }
          }
          return prev;
        }
        return [
          ...prev,
          {
            playerId: winnerId,
            holeNumber: entry.holeNumber,
            eventType: 'mysteryWinner',
            eventValue: subgameId,
            occurredAt: timestamp,
          },
        ];
      });
    },
    [
      apiBaseUrl,
      effectiveClaimClientId,
      mysteryEntriesForHole,
      mysteryWinnersBySubgame,
      roundId,
      socketClient,
      user?.token,
      isAnonymousReadOnlyMode,
      claimSession?.claimAccessToken,
    ]
  );
  const displayTotals: DisplayTotal[] = useMemo(
    () =>
      buildDisplayTotals({
        leaderboardTotals,
        totals,
        gameResults,
        leaderboardGamePoints,
        players,
        tourTotals,
        unknownPlayerLabel: t('hole.unknownPlayer'),
      }),
    [gameResults, leaderboardGamePoints, leaderboardTotals, players, totals, tourTotals]
  );
  const tourLeaderKeys = useMemo(() => buildTourLeaderKeys(displayTotals), [displayTotals]);
  const tourPlacementByKey = useMemo(() => buildTourPlacementByKey(displayTotals), [displayTotals]);
  const roundPlacementByKey = useMemo(() => buildRoundPlacementByKey(displayTotals), [displayTotals]);
  const renderPlacementBadge = useCallback((roundPlace?: number, tourPlace?: number) => {
    const place = (roundPlace && roundPlace >= 1 && roundPlace <= 3)
      ? roundPlace
      : (tourPlace && tourPlace >= 1 && tourPlace <= 3)
        ? tourPlace
        : null;
    if (!place) return null;
    const medal = place === 1 ? '🥇' : place === 2 ? '🥈' : '🥉';
    return (
      <View style={styles.roundPlacementBadge}>
        <Text style={styles.roundPlacementMedal}>{medal}</Text>
      </View>
    );
  }, []);
  const orderedTeams = useMemo(() => buildOrderedTeams(teams), [teams]);
  const gameResultPlayers = useMemo(
    () =>
      buildGameResultPlayers({
        players,
        orderedTeams,
        gameResults,
      }),
    [players, gameResults, orderedTeams]
  );
  const gameDefinitionNameById = useMemo(() => {
    const map: Record<string, string> = {};
    gameDefinitionNames.forEach((def) => {
      if (!def.id) return;
      const name = getGameDisplayName(
        {
          id: def.id,
          name: def.name ?? (def as { Name?: string }).Name,
          info: def.info ?? (def as { Info?: string }).Info,
          description: def.description ?? (def as { Description?: string }).Description,
        },
        t('hole.gameFallbackName')
      );
      if (name) {
        map[def.id] = name;
      }
    });
    return map;
  }, [gameDefinitionNames]);
  const gameResultRows = useMemo(
    () =>
      buildGameResultRows({
        orderedTeams,
        players,
        selectedGameDefinitionIds,
        gameResults,
        leaderboardGamePoints,
        gameDefinitionNameById,
        gameDefinitionTypes,
        gameResultPlayers,
        gameFallbackName: t('hole.gameFallbackName'),
      }),
    [
      gameDefinitionNameById,
      gameDefinitionTypes,
      gameResults,
      gameResultPlayers,
      leaderboardGamePoints,
      orderedTeams,
      players,
      selectedGameDefinitionIds,
    ]
  );
  const hazardIconsByPlayer = useMemo(() => {
    const playerIdByName = new Map(players.map((p) => [normalizePlayerKey(p.name), p.id]));
    const result = new Map<string, Array<typeof WaterIcon>>();
    const typesByPlayer = new Map<string, Set<string>>();
    Object.entries(gameResults).forEach(([, game]) => {
      const hazardType = hazardTypeFromName(game.name);
      if (!hazardType) return;
      if (!selectedHazardTypes.has(hazardType)) return;
      const icon = hazardIconForGameName(game.name);
      if (!icon) return;
      Object.entries(game.scorePoints).forEach(([playerKey, points]) => {
        if (points >= 0) return;
        const normalizedKey = normalizePlayerKey(playerKey);
        const resolvedId = playerIdByName.get(normalizedKey) ?? playerKey;
        const list = result.get(resolvedId) ?? [];
        if (!list.includes(icon)) {
          list.push(icon);
          result.set(resolvedId, list);
        }
        const types = typesByPlayer.get(resolvedId) ?? new Set<string>();
        types.add(hazardType);
        typesByPlayer.set(resolvedId, types);
      });
    });

    return {
      getFor: (key: string, name: string) => {
        const direct = result.get(key);
        if (direct) return direct;
        const id = playerIdByName.get(normalizePlayerKey(name));
        return id ? result.get(id) ?? [] : [];
      },
      getTypesFor: (key: string, name: string) => {
        const direct = typesByPlayer.get(key);
        if (direct) return direct;
        const id = playerIdByName.get(normalizePlayerKey(name));
        return id ? typesByPlayer.get(id) ?? new Set<string>() : new Set<string>();
      },
    };
  }, [gameResults, players, selectedHazardTypes]);
  const selectedPlayer = useMemo(() => {
    if (!selectedPlayerId) return null;
    const match = players.find((p) => p.id === selectedPlayerId) ?? displayTotals.find((t) => t.key === selectedPlayerId);
    if (!match) return { id: selectedPlayerId, name: selectedPlayerId };
    if ('name' in match) return { id: selectedPlayerId, name: match.name };
    return { id: selectedPlayerId, name: selectedPlayerId };
  }, [displayTotals, players, selectedPlayerId]);
  const resolveGameRowIcon = useCallback(
    (gameId: string, gameName: string) => {
      const sourceName = gameResults[gameId]?.name ?? gameDefinitionNameById[gameId] ?? gameName;
      const gameSlug = normalizeGameSlug(sourceName);
      if (GAME_ICON_MAP[gameSlug]) {
        return GAME_ICON_MAP[gameSlug];
      }
      if (gameSlug.includes('penalty')) return PenaltyIcon;
      if (gameSlug.includes('3-putts') || gameSlug.includes('three-putts')) return ThreePuttIcon;
      if (gameSlug.includes('sandy-par') || gameSlug.includes('bunker')) return BunkerIcon;
      if (gameSlug.includes('wet-par') || gameSlug.includes('water')) return WaterIcon;
      if (gameSlug.includes('birdie')) return BirdieImage;
      return GolfballIcon;
    },
    [gameDefinitionNameById, gameResults]
  );
  const selectedPlayerGamePoints = useMemo(() => {
    if (!selectedPlayer) return [];
    const selectedGameIds = new Set(selectedGameDefinitionIds);
    const normalizedSelectedId = normalizePlayerKey(selectedPlayer.id);
    const normalizedSelectedName = normalizePlayerKey(selectedPlayer.name);
    const leaderboardPoints =
      leaderboardGamePoints[selectedPlayer.id] ??
      leaderboardGamePoints[selectedPlayer.name] ??
      Object.entries(leaderboardGamePoints).find(([key]) => {
        const normalizedKey = normalizePlayerKey(key);
        return normalizedKey === normalizedSelectedId || normalizedKey === normalizedSelectedName;
      })?.[1] ??
      [];
    const leaderboardPointsByGameId = new Map(
      leaderboardPoints
        .filter((gp) => typeof gp.gameId === 'string' && gp.gameId.trim().length > 0)
        .map((gp) => [gp.gameId as string, { points: gp.roundGamePoints, position: gp.position }])
    );

    const availableGameIds = new Set([
      ...Array.from(leaderboardPointsByGameId.keys()),
      ...Object.keys(gameResults),
    ]);
    const selectedAvailableIds = Array.from(selectedGameIds).filter((id) => availableGameIds.has(id));
    const candidateGameIds =
      selectedAvailableIds.length > 0
        ? selectedAvailableIds
        : Array.from(availableGameIds);

    return candidateGameIds.map((gameId) => {
      const leaderboardValue = leaderboardPointsByGameId.get(gameId);
      const gameName = getGameDisplayName(
        { id: gameId, name: gameResults[gameId]?.name ?? gameDefinitionNameById[gameId] },
        t('hole.gameFallbackName')
      );
      return {
        id: gameId,
        name: gameName,
        points: leaderboardValue?.points ?? 0,
        position: leaderboardValue?.position,
        icon: resolveGameRowIcon(gameId, gameName),
      };
    });
  }, [gameDefinitionNameById, gameResults, leaderboardGamePoints, resolveGameRowIcon, selectedGameDefinitionIds, selectedPlayer]);
  const loggedInRoundPlayer = useMemo(() => {
    const userId = normalizePlayerKey(user?.id);
    if (loggedInPlayerId) {
      const byResolvedId = players.find((player) => normalizePlayerKey(player.id) === normalizePlayerKey(loggedInPlayerId));
      if (byResolvedId) {
        return byResolvedId;
      }
    }
    if (userId) {
      return players.find((player) => normalizePlayerKey(player.id) === userId) ?? null;
    }
    return null;
  }, [loggedInPlayerId, players, user?.id]);
  const holeMenuItems = useMemo(() => {
    const loggedInPlayerKey = normalizePlayerKey(loggedInRoundPlayer?.id ?? loggedInPlayerId ?? null);
    const loggedInPlayerNameKey = normalizePlayerKey(loggedInRoundPlayer?.name ?? null);

    const ownEntriesByHole = new Map<number, HoleEntry>();
    const playedHoles = new Set<number>();

    allHoleEntries.forEach((entry) => {
      if (entry.holeNumber < 1 || entry.holeNumber > HOLE_MENU_COUNT) {
        return;
      }

      playedHoles.add(entry.holeNumber);

      const entryPlayerIdKey = normalizePlayerKey(entry.playerId ?? null);
      const entryPlayerNameKey = normalizePlayerKey(entry.playerName ?? null);
      const isOwnEntryById = !!loggedInPlayerKey && !!entryPlayerIdKey && entryPlayerIdKey === loggedInPlayerKey;
      const isOwnEntryByName = !isOwnEntryById && !!loggedInPlayerNameKey && entryPlayerNameKey === loggedInPlayerNameKey;

      if (isOwnEntryById || isOwnEntryByName) {
        const existing = ownEntriesByHole.get(entry.holeNumber);
        if (!existing) {
          ownEntriesByHole.set(entry.holeNumber, entry);
          return;
        }
        const existingHasScore = existing.grossStrokes !== null && existing.grossStrokes !== undefined;
        const candidateHasScore = entry.grossStrokes !== null && entry.grossStrokes !== undefined;
        const existingCreatedAt = Date.parse(existing.createdAt ?? '');
        const candidateCreatedAt = Date.parse(entry.createdAt ?? '');
        const existingTs = Number.isFinite(existingCreatedAt) ? existingCreatedAt : Number.MIN_SAFE_INTEGER;
        const candidateTs = Number.isFinite(candidateCreatedAt) ? candidateCreatedAt : Number.MIN_SAFE_INTEGER;
        if (candidateTs === existingTs && candidateHasScore !== existingHasScore) {
          if (candidateHasScore) {
            ownEntriesByHole.set(entry.holeNumber, entry);
          }
          return;
        }
        if (!Number.isFinite(existingCreatedAt) && !Number.isFinite(candidateCreatedAt) && candidateHasScore && !existingHasScore) {
          ownEntriesByHole.set(entry.holeNumber, entry);
          return;
        }
        if (candidateTs >= existingTs) {
          ownEntriesByHole.set(entry.holeNumber, entry);
        }
      }
    });

    return Array.from({ length: HOLE_MENU_COUNT }, (_, index) => {
      const number = index + 1;
      const ownEntry = ownEntriesByHole.get(number);
      const ownScore = ownEntry
        ? ownEntry.grossStrokes === null
          ? ownEntry.pickup
            ? t('hole.pickup')
            : t('hole.menuScoreMissing')
          : String(ownEntry.grossStrokes)
        : t('hole.menuScoreMissing');
      return {
        holeNumber: number,
        isPlayed: playedHoles.has(number),
        ownScore,
      };
    });
  }, [allHoleEntries, loggedInPlayerId, loggedInRoundPlayer?.id, loggedInRoundPlayer?.name]);
  const closeHoleMenu = useCallback(() => {
    setHoleMenuOpen(false);
  }, []);
  const selectHoleFromMenu = useCallback(
    (targetHoleNumber: number) => {
      setHoleMenuOpen(false);
      if (targetHoleNumber === holeNumber) {
        return;
      }
      navigateToHole(targetHoleNumber);
    },
    [holeNumber, navigateToHole]
  );
  const holeMenuPopup = holeMenuOpen ? (
    <View style={styles.holeMenuPopupLayer} pointerEvents="box-none">
      <Pressable style={styles.holeMenuPopupBackdrop} onPress={closeHoleMenu}>
        <Pressable style={styles.holeMenuPopupCard} onPress={() => undefined}>
          <Text style={styles.modalTitle}>{t('hole.menuTitle')}</Text>
          <ScrollView style={styles.holeMenuScroll} contentContainerStyle={styles.holeMenuScrollContent}>
            {holeMenuItems.map((item) => (
              <Pressable
                key={`hole-menu-${item.holeNumber}`}
                style={[styles.holeMenuRow, item.holeNumber === holeNumber && styles.holeMenuRowCurrent]}
                onPress={() => selectHoleFromMenu(item.holeNumber)}
              >
                <View style={styles.holeMenuRowLeft}>
                  <Text style={styles.holeMenuRowTitle}>{`${t('hole.title')} ${item.holeNumber}`}</Text>
                  {item.holeNumber === holeNumber ? <Text style={styles.holeMenuCurrent}>{t('hole.menuCurrent')}</Text> : null}
                </View>
                <View style={styles.holeMenuRowRight}>
                  <Text style={styles.holeMenuStatus}>{item.isPlayed ? t('hole.menuPlayed') : t('hole.menuNotPlayed')}</Text>
                  <Text style={styles.holeMenuScore}>{t('hole.menuMyScore', { score: item.ownScore })}</Text>
                </View>
              </Pressable>
            ))}
          </ScrollView>
        </Pressable>
      </Pressable>
    </View>
  ) : null;
  const gamePositionsByPlayer = useMemo(() => {
    const map: Record<string, Record<string, number>> = {};
    const positionSeed: Record<string, Record<string, number>> = {};
    leaderboardTotals.forEach((total) => {
      const playerId = total.playerId;
      if (!playerId || !total.roundGamePoints) return;
      total.roundGamePoints.forEach((gp) => {
        const gameId = gp.gameId ?? 'game';
        map[gameId] ??= {};
        map[gameId][playerId] = gp.roundGamePoints;
        if (gp.position !== undefined) {
          positionSeed[gameId] ??= {};
          positionSeed[gameId][playerId] = gp.position;
        }
      });
    });
    const positionMap: Record<string, Record<string, number>> = {};
    Object.entries(map).forEach(([gameId, pointsByPlayer]) => {
      const isTeamGame = gameDefinitionTypes[gameId] === 'team';
      if (isTeamGame && orderedTeams.length > 0) {
        const teamEntries = orderedTeams
          .map((team) => {
            const members = team.players ?? [];
            const memberPoints = members
              .map((pid) => pointsByPlayer[pid])
              .filter((value): value is number => typeof value === 'number');
            if (memberPoints.length === 0) return null;
            return { teamId: team.id, points: Math.max(...memberPoints), players: members };
          })
          .filter((entry): entry is { teamId: string; points: number; players: string[] } => entry !== null);
        teamEntries.sort((a, b) => b.points - a.points);
        let positionIndex = 1;
        for (let i = 0; i < teamEntries.length; ) {
          const currentPoints = teamEntries[i].points;
          const tieGroup = teamEntries.slice(i).filter((e) => e.points === currentPoints);
          tieGroup.forEach((entry) => {
            entry.players.forEach((playerId) => {
              positionMap[gameId] ??= {};
              positionMap[gameId][playerId] = positionIndex;
            });
          });
          positionIndex += tieGroup.length;
          i += tieGroup.length;
        }
        return;
      }
      if (!isTeamGame && positionSeed[gameId]) {
        positionMap[gameId] = { ...positionSeed[gameId] };
        return;
      }
      const entries = Object.entries(pointsByPlayer).map(([playerId, points]) => ({ playerId, points }));
      entries.sort((a, b) => b.points - a.points);
      let positionIndex = 1;
      for (let i = 0; i < entries.length; ) {
        const currentPoints = entries[i].points;
        const tieGroup = entries.slice(i).filter((e) => e.points === currentPoints);
        tieGroup.forEach((entry) => {
          positionMap[gameId] ??= {};
          positionMap[gameId][entry.playerId] = positionIndex;
        });
        positionIndex += tieGroup.length;
        i += tieGroup.length;
      }
    });
    return positionMap;
  }, [gameDefinitionTypes, leaderboardTotals, orderedTeams]);
  const positionShareByGame = useMemo(() => {
    const sharedByGame: Record<string, Record<string, boolean>> = {};
    const pointsByGame: Record<string, Record<string, number>> = {};
    leaderboardTotals.forEach((total) => {
      const playerId = total.playerId;
      if (!playerId || !total.roundGamePoints) return;
      total.roundGamePoints.forEach((gp) => {
        const gameId = gp.gameId ?? 'game';
        pointsByGame[gameId] ??= {};
        pointsByGame[gameId][playerId] = gp.roundGamePoints;
      });
    });

    Object.entries(pointsByGame).forEach(([gameId, pointsByPlayer]) => {
      const isTeamGame = gameDefinitionTypes[gameId] === 'team';
      if (isTeamGame && orderedTeams.length > 0) {
        const teamEntries = orderedTeams
          .map((team) => {
            const members = team.players ?? [];
            const memberPoints = members
              .map((pid) => pointsByPlayer[pid])
              .filter((value): value is number => typeof value === 'number');
            if (memberPoints.length === 0) return null;
            return { teamId: team.id, points: Math.max(...memberPoints), players: members };
          })
          .filter((entry): entry is { teamId: string; points: number; players: string[] } => entry !== null);
        teamEntries.sort((a, b) => b.points - a.points);
        for (let i = 0; i < teamEntries.length; ) {
          const currentPoints = teamEntries[i].points;
          const tieGroup = teamEntries.slice(i).filter((e) => e.points === currentPoints);
          const isShared = tieGroup.length > 1;
          if (isShared) {
            tieGroup.forEach((entry) => {
              entry.players.forEach((playerId) => {
                sharedByGame[gameId] ??= {};
                sharedByGame[gameId][playerId] = true;
              });
            });
          }
          i += tieGroup.length;
        }
        return;
      }

      const positionCounts: Record<number, number> = {};
      Object.entries(pointsByPlayer).forEach(([playerId]) => {
        const pos = gamePositionsByPlayer[gameId]?.[playerId];
        if (!pos) return;
        positionCounts[pos] = (positionCounts[pos] ?? 0) + 1;
      });
      Object.entries(pointsByPlayer).forEach(([playerId]) => {
        const pos = gamePositionsByPlayer[gameId]?.[playerId];
        if (!pos) return;
        if ((positionCounts[pos] ?? 0) > 1) {
          sharedByGame[gameId] ??= {};
          sharedByGame[gameId][playerId] = true;
        }
      });
    });

    return sharedByGame;
  }, [gameDefinitionTypes, gamePositionsByPlayer, leaderboardTotals, orderedTeams]);
  const defaultTeamColor = appColors.text.primary;
  const defaultScoreColor = '#A1D493';
  const hasSelectedTeamGame = useMemo(
    () =>
      selectedGameDefinitionIds.some(
        (gameId) => (gameDefinitionTypes[gameId] ?? '').toLowerCase() === 'team'
      ),
    [gameDefinitionTypes, selectedGameDefinitionIds]
  );
  const hasActiveTeamPlay = hasSelectedTeamGame && orderedTeams.length > 0;
  const teamColors = useMemo(() => {
    const palette = [appColors.text.primary, '#8FA08B', '#A1D493', '#44504a', '#6a5c2d', '#7a3a46'];
    const map: Record<string, string> = {};
    if (orderedTeams.length > 0) {
      orderedTeams.forEach((team, idx) => {
        const color = palette[idx % palette.length];
        (team.players ?? []).forEach((pid) => {
          map[pid] = color;
        });
      });
      return map;
    }
    gameResultPlayers.forEach((p, idx) => {
      const teamIdx = Math.floor(idx / 2);
      map[p.id] = palette[teamIdx % palette.length];
    });
    return map;
  }, [gameResultPlayers, orderedTeams]);
  const teamColorByKey = useMemo(() => {
    const map: Record<string, string> = { ...teamColors };
    players.forEach((player) => {
      const color = teamColors[player.id];
      if (!color) return;
      map[player.name] = color;
      map[normalizePlayerKey(player.name)] = color;
      map[normalizePlayerKey(player.id)] = color;
    });
    return map;
  }, [players, teamColors]);
  const teamLabelByPlayer = useMemo(() => {
    const map: Record<string, string> = {};
    orderedTeams.forEach((team) => {
      const teamName = (team.name ?? '').trim();
      const label = teamName.length > 0 && !isUuidLike(teamName) ? teamName : null;
      if (!label) {
        return;
      }
      (team.players ?? []).forEach((pid) => {
        map[pid] = label;
      });
    });
    players.forEach((player) => {
      const label = map[player.id];
      if (label) {
        map[player.name] = label;
        map[normalizePlayerKey(player.name)] = label;
        map[normalizePlayerKey(player.id)] = label;
      }
    });
    return map;
  }, [orderedTeams, players]);
  const resolveTeamLabel = useCallback(
    (key?: string) => {
      if (!key) return null;
      return teamLabelByPlayer[normalizePlayerKey(key)] ?? teamLabelByPlayer[key] ?? null;
    },
    [teamLabelByPlayer]
  );
  const resolveTeamColor = useCallback(
    (key?: string) => {
      if (!hasActiveTeamPlay) return defaultTeamColor;
      if (!key) return defaultTeamColor;
      return teamColorByKey[normalizePlayerKey(key)] ?? teamColorByKey[key] ?? defaultTeamColor;
    },
    [defaultTeamColor, hasActiveTeamPlay, teamColorByKey]
  );
  const hasRealtimeGameData = gameResultRows.length > 0 || Object.keys(gameResults).length > 0;
  const shouldShowGamePanels = hasSelectedGames || hasRealtimeGameData;
  const leaderboardPanel = !shouldShowGamePanels ? (
    <View style={styles.scoreboard} lightColor="#07200D" darkColor="#07200D">
      <Text style={styles.scoreboardEmpty}>{t('hole.noGamesSelected')}</Text>
    </View>
  ) : displayTotals.length > 0 ? (
    <View style={styles.scoreboard} lightColor="#07200D" darkColor="#07200D">
      <View style={styles.scoreboardColumnsHeader}>
        <Text style={styles.scoreboardNameHeader}>{t('hole.leaderboardShortTitle')}</Text>
        <View style={styles.scoreboardScores}>
          <Text style={styles.scoreboardScoreColumnHeader}>{t('hole.roundColumn')}</Text>
          <Text style={styles.scoreboardScoreColumnHeader}>{t('hole.tourColumn')}</Text>
        </View>
      </View>
      {displayTotals.map((total) => (
        <Pressable
          key={total.key}
          style={styles.scoreboardRow}
          onPress={() => setSelectedPlayerId(total.key)}
        >
          {(() => {
            const placement =
              tourPlacementByKey[normalizePlayerKey(total.key)] ??
              tourPlacementByKey[normalizePlayerKey(total.name)];
            const roundPlacement =
              roundPlacementByKey[normalizePlayerKey(total.key)] ??
              roundPlacementByKey[normalizePlayerKey(total.name)];
            return (
          <View style={styles.scoreboardNameRow}>
            <View style={styles.scoreboardNameContent}>
              {renderPlacementBadge(roundPlacement, placement)}
              {mysteryWinnerIdsForHole.has(total.key) ? (
                <Image source={MysteryPrizeIcon} style={styles.mysteryWinnerIcon} resizeMode="contain" />
              ) : null}
              {hazardIconsByPlayer.getFor(total.key, total.name).map((icon, idx) => (
                <Image key={`${total.key}-hazard-${idx}`} source={icon} style={styles.hazardIcon} resizeMode="contain" />
              ))}
              <Text style={[styles.scoreboardName, { color: resolveTeamColor(total.key ?? total.name) }]}>
                {`${(() => {
                  const placement =
                    tourPlacementByKey[normalizePlayerKey(total.key)] ??
                    tourPlacementByKey[normalizePlayerKey(total.name)];
                  const isTourLeader =
                    tourLeaderKeys.has(normalizePlayerKey(total.key)) || tourLeaderKeys.has(normalizePlayerKey(total.name));
                  return isTourLeader && !placement ? '🏆 ' : '';
                })()}${total.name}`}
                {(() => {
                  const teamLabel = resolveTeamLabel(total.key ?? total.name);
                  return teamLabel ? ` (${teamLabel})` : '';
                })()}
              </Text>
            </View>
            <View style={styles.infoIconSmall}>
              <Text style={styles.infoIconSmallText}>i</Text>
            </View>
          </View>
            );
          })()}
          <View style={styles.scoreboardScores}>
            <Text style={[styles.scoreboardValue, { color: resolveTeamColor(total.key ?? total.name) }]}>
              {total.roundPoints !== undefined ? total.roundPoints : t('hole.roundPointsMissing')}
            </Text>
            <Text style={[styles.scoreboardValue, { color: resolveTeamColor(total.key ?? total.name) }]}>
              {total.totalStrokes}
            </Text>
          </View>
        </Pressable>
      ))}
    </View>
  ) : null;
  const gameResultsPanel = gameResultRows.length > 0 ? (
    <View style={styles.scoreboard} lightColor="#07200D" darkColor="#07200D">
      <View style={styles.gameResultsFrame}>
        <View style={[styles.gameResultsRow, styles.gameResultsHeader]}>
          <Text style={[styles.scoreboardTitle, styles.gameResultsName]}>{t('hole.gamesTitle')}</Text>
          {gameResultPlayers.map((p) => (
            <Text
              key={p.id}
              style={[styles.gameResultsPlayer, { color: hasActiveTeamPlay ? (teamColors[p.id] ?? defaultTeamColor) : defaultTeamColor }]}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {compactPlayerName(p.name)}
            </Text>
          ))}
        </View>
        {gameResultRows.map((row) => (
          <View key={row.key} style={styles.gameResultsRow}>
            <Text style={styles.gameResultsName} numberOfLines={1} ellipsizeMode="tail">
              {row.name}
            </Text>
            {row.scores.map((score, idx) => (
              <View key={`${row.key}-${idx}`} style={styles.gameResultCell}>
                <View
                  style={[
                    styles.gameResultValueWrapper,
                    !row.isHazardGame && row.sharedLeaders?.[idx] ? styles.gameResultLeaderShared : null,
                  ]}
                >
                  <View style={styles.gameResultValueContent}>
                    {!row.isHazardGame && row.leaders[idx] ? <Text style={styles.gameResultMedalText}>🥇</Text> : null}
                    {row.isHazardGame && row.losers?.[idx] ? <Text style={styles.gameResultFishText}>🐟</Text> : null}
                    <Text
                      style={[
                        styles.gameResultValue,
                        {
                          color: hasActiveTeamPlay
                            ? (teamColors[gameResultPlayers[idx]?.id ?? ''] ?? defaultScoreColor)
                            : defaultScoreColor,
                        },
                      ]}
                    >
                      {score}
                    </Text>
                  </View>
                </View>
              </View>
            ))}
          </View>
        ))}
      </View>
    </View>
  ) : null;
  const holeHeader = (
    <HoleHeaderCard
      holeNumber={holeNumber}
      parLabel={parLabel}
      hcpLabel={hcpLabel}
      showLeaderboardLabel={false}
      hasMysteryPrizeForHole={hasMysteryPrizeForHole}
      mysteryPrizeText={mysteryPrizeText}
      onOpenMystery={() => setMysteryModalOpen(true)}
      holeName={currentHoleMeta.name}
      holeDescription={currentHoleMeta.description}
      showHoleDetails={showHoleDetails}
      onToggleDetails={() => setShowHoleDetails((prev) => !prev)}
    />
  );
  const viewModel: HoleScreenViewModel = {
    state: {
      roundId,
      holeNumber,
      players,
      holeEntries,
      totals,
      leaderboardTotals,
      gameResults,
      holeMeta,
      holePar,
      teams,
      holeEvents,
      subgames,
      loading,
      error,
    },
    derived: {
      displayTotals,
      gameResultRows,
      hasSelectedGames,
      strokeIndexForHole,
    },
    actions: {
      navigateToHole,
      finishRound,
    },
    uiState: {
      mysteryModalOpen,
      nextMysteryModalOpen,
      scorePopupPlayerId,
    },
  };

  if (!isNavigationReady) {
    return null;
  }

  if (Platform.OS === 'web' && !isClient) {
    return null;
  }

  if (!user && !claimSessionLoaded) {
    return null;
  }

  if (!user && !isGuestClaimMode) {
    return <Redirect href="/" />;
  }

  if (!roundId) {
    return (
      <View style={styles.container}>
        <Text style={styles.subtitle}>{t('hole.missingRound')}</Text>
        <GoffyButton
          label={t('hole.goPlayers')}
          onPress={() => {
            const debugSuffix = debug ? '?debug=true' : '';
            const nextPath = tourId ? `/tour/${tourId}/round-init${debugSuffix}` : '/tournaments';
            router.push(nextPath);
          }}
          tone="accent"
          oldButtonStyle={styles.primaryButton}
          oldTextStyle={styles.primaryButtonText}
          style={styles.primaryButtonImageMode}
          textStyle={styles.primaryButtonImageText}
        />
      </View>
    );
  }

  if (!navState?.key) {
    return null;
  }

  return (
      <View style={[styles.compactRoot, { minHeight: windowHeight }]}>
        <ScrollView
          contentContainerStyle={[styles.scrollContent, styles.compactScrollContent]}
          style={[styles.scroll, styles.compactScroll]}
          showsVerticalScrollIndicator
          nestedScrollEnabled
          keyboardShouldPersistTaps="handled"
          scrollEnabled={!scorePopupPlayerId && !isScoreSwipeActive}
        >
          <View style={[styles.pageBackground, { minHeight: windowHeight }]}>
            <View style={[styles.container, styles.containerCompact]}>
              {holeHeader}
              {debug ? (
                <>
                  <View style={styles.metaRow}>
                    {roundId ? <Text style={styles.meta}>{t('hole.roundId', { id: roundId })}</Text> : null}
                  </View>
                  {tourId ? <Text style={styles.meta}>{t('hole.tourId', { id: tourId })}</Text> : null}
                </>
              ) : null}
              {viewModel.state.loading ? <Text style={styles.subtitle}>{t('course.loading')}</Text> : null}
              {viewModel.state.error ? <Text style={styles.error}>{viewModel.state.error}</Text> : null}
              {leaderboardPanel}
              {viewModel.state.players.length === 0 && !viewModel.state.loading ? <Text style={styles.subtitle}>{t('hole.noPlayers')}</Text> : null}
              <View style={styles.compactList}>
              {viewModel.state.players.map((player, playerIndex) => (
                (() => {
                  const existing = hasExactHoleData ? holeEntries.find((entry) => {
                    if (entry.holeNumber !== holeNumber) return false;
                    const entryName = (entry.playerName ?? '').trim().toLowerCase();
                    const playerName = player.name.trim().toLowerCase();
                    return entry.playerId === player.id || entryName === playerName || entry.playerName === player.id;
                  }) : undefined;
                  const selectedFromExisting =
                    existing && existing.grossStrokes !== null
                      ? String(existing.grossStrokes)
                      : existing?.pickup
                        ? 'Pickup'
                        : undefined;
                  const selectedScore =
                    hasExactHoleData
                      ? (
                        scores[player.id] ??
                        scores[player.name] ??
                        selectedFromExisting
                      )
                      : undefined;
                  const handicapStrokes =
                    existing?.handicapStrokes ??
                    computeStrokesReceivedForHole(player.handicap ?? null, strokeIndexForHole);
                  const targetGross = parForHole && parForHole > 0 ? parForHole + handicapStrokes : null;
                  const swipeDefaultScore =
                    targetGross && targetGross > 0
                      ? targetGross
                      : parForHole && parForHole > 0
                        ? parForHole
                        : 4;
                  const swipeStartScore = normalizeSwipeScoreValue(selectedScore, swipeDefaultScore);
                  const compactRowScoreSwipeResponder = PanResponder.create({
                    onStartShouldSetPanResponder: () => false,
                    onStartShouldSetPanResponderCapture: () => false,
                    onMoveShouldSetPanResponder: (_, gesture) => {
                      const { dx, dy } = gesture;
                      const absDx = Math.abs(dx);
                      const absDy = Math.abs(dy);
                      const primary = absDx >= absDy ? absDx : absDy;
                      const secondary = absDx >= absDy ? absDy : absDx;
                      return primary > secondary * 1.1 && primary > 10;
                    },
                    onMoveShouldSetPanResponderCapture: (_, gesture) => {
                      const { dx, dy } = gesture;
                      const absDx = Math.abs(dx);
                      const absDy = Math.abs(dy);
                      const primary = absDx >= absDy ? absDx : absDy;
                      const secondary = absDx >= absDy ? absDy : absDx;
                      return primary > secondary * 1.1 && primary > 10;
                    },
                    onPanResponderGrant: () => {
                      setIsScoreSwipeActive(true);
                      rowScoreSwipeRef.current[player.id] = {
                        startScore: swipeStartScore,
                        lastStep: 0,
                        lastScoreValue: null,
                        direction: 0,
                        extremeDx: 0,
                      };
                      if (!selectedScore) {
                        const startValue = formatSwipeScoreValue(swipeStartScore);
                        rowScoreSwipeRef.current[player.id].lastScoreValue = startValue;
                        applySwipeScoreValue(player, swipeStartScore, { suppressCelebration: true, suppressAutoAdvance: true });
                      }
                    },
                    onPanResponderMove: (_, gesture) => {
                      const thresholdPx = 18;
                      const primaryDelta = Math.abs(gesture.dx) >= Math.abs(gesture.dy) ? gesture.dx : gesture.dy;
                      const existingSession = rowScoreSwipeRef.current[player.id];
                      const session =
                        existingSession ??
                        { startScore: swipeStartScore, lastStep: 0, lastScoreValue: null, direction: 0 as -1 | 0 | 1, extremeDx: 0 };
                      if (!existingSession) {
                        rowScoreSwipeRef.current[player.id] = session;
                      }
                      if (Math.abs(primaryDelta) < 8) {
                        return;
                      }
                      const nextDirection: -1 | 1 = primaryDelta >= 0 ? 1 : -1;
                      if (session.direction === 0 || session.direction !== nextDirection) {
                        const currentValue = scores[player.id] ?? scores[player.name] ?? null;
                        const currentParsed = currentValue ? Number.parseInt(currentValue, 10) : Number.NaN;
                        const currentNumeric = Number.isFinite(currentParsed)
                          ? Math.max(1, Math.min(MAX_SWIPE_SCORE, currentParsed))
                          : session.startScore;
                        session.startScore = currentNumeric;
                        session.direction = nextDirection;
                        session.extremeDx = primaryDelta;
                        session.lastStep = 0;
                      } else {
                        session.extremeDx = session.direction > 0
                          ? Math.max(session.extremeDx, primaryDelta)
                          : Math.min(session.extremeDx, primaryDelta);
                      }
                      const stepDelta = session.direction > 0
                        ? Math.floor(session.extremeDx / thresholdPx)
                        : Math.ceil(session.extremeDx / thresholdPx);
                      if (stepDelta === session.lastStep) return;
                      session.lastStep = stepDelta;
                      const nextNumeric = session.startScore + stepDelta;
                      const nextValue = formatSwipeScoreValue(nextNumeric);
                      session.lastScoreValue = nextValue;
                      applySwipeScoreValue(player, nextNumeric, { suppressCelebration: true, suppressAutoAdvance: true });
                    },
                    onPanResponderRelease: () => {
                      setIsScoreSwipeActive(false);
                      const session = rowScoreSwipeRef.current[player.id];
                      if (session?.lastScoreValue) {
                        const numeric = Number.parseInt(session.lastScoreValue, 10);
                        const parToSend = holePar ?? currentHoleMeta.par ?? 4;
                        if (Number.isFinite(numeric) && numeric === parToSend - 1) {
                          triggerBirdies();
                        }
                      }
                      delete rowScoreSwipeRef.current[player.id];
                    },
                    onPanResponderTerminate: () => {
                      setIsScoreSwipeActive(false);
                      delete rowScoreSwipeRef.current[player.id];
                    },
                    onPanResponderTerminationRequest: () => true,
                  });
                  return (
                <View key={player.id} style={styles.compactRow}>
                  <View style={styles.compactInfoColumn}>
                    <View style={styles.compactNameRow}>
                      <View style={styles.compactNameBlock}>
                      {(() => {
                        const avatarUrl = player.imageThumbKey ?? player.imageKey ?? null;
                        return (
                          <View style={styles.compactNameLine}>
                            <View style={styles.compactAvatarWrap}>
                              {avatarUrl ? (
                                <Image source={{ uri: avatarUrl }} style={styles.compactAvatar} />
                              ) : null}
                            </View>
                            <Text style={styles.compactName}>{player.name}</Text>
                            {renderPlacementBadge(
                              roundPlacementByKey[normalizePlayerKey(player.id)] ??
                                roundPlacementByKey[normalizePlayerKey(player.name)],
                              tourPlacementByKey[normalizePlayerKey(player.id)] ??
                                tourPlacementByKey[normalizePlayerKey(player.name)]
                            )}
                          </View>
                        );
                      })()}
                      {strokeIndexForHole !== null
                        ? (() => {
                            const hcapStrokes =
                              ((hasExactHoleData ? holeEntries.find((h) => h.playerId === player.id) : undefined)?.handicapStrokes ??
                                computeStrokesReceivedForHole(player.handicap ?? null, strokeIndexForHole)) ?? 0;
                            const keyNorm = normalizePlayerKey(player.id);
                            const nameNorm = normalizePlayerKey(player.name);
                            const isTourLeader = tourLeaderKeys.has(keyNorm) || tourLeaderKeys.has(nameNorm);
                            const placement =
                              tourPlacementByKey[keyNorm] ??
                              tourPlacementByKey[nameNorm];
                            return (
                              <View style={styles.compactHandicapRow}>
                                <Text style={styles.compactHandicapText}>{`+${hcapStrokes}`}</Text>
                                {isTourLeader && !placement ? <Text style={styles.compactLeaderTrophy}> 🥇</Text> : null}
                              </View>
                            );
                          })()
                        : null}
                      </View>
                    </View>
                  </View>
                  {(() => {
                    const displayScore = selectedScore;
                    const stablefordPoints =
                      isStablefordGame && selectedScore && selectedScore !== 'Pickup' && parForHole
                        ? Math.max(
                            0,
                            2 +
                              parForHole -
                              computeNetForHole({
                                gross: selectedScore,
                                handicap: player.handicap ?? null,
                                holeIndex: strokeIndexForHole,
                                par: parForHole,
                              }).netStrokes
                          )
                        : null;
                    const compactCounts = resolveHazardCountsForPlayer(player);
                    const compactHazardBadges = [
                      {
                        key: 'water',
                        label: t('hole.event.water'),
                        count: compactCounts.water,
                        style: styles.compactScoreHazardBadgeWater,
                        textStyle: styles.compactScoreHazardBadgeTextLight,
                      },
                      {
                        key: 'sand',
                        label: t('hole.event.bunker'),
                        count: compactCounts.bunker,
                        style: styles.compactScoreHazardBadgeSand,
                        textStyle: styles.compactScoreHazardBadgeTextLight,
                      },
                      {
                        key: 'threeputt',
                        label: t('hole.event.threePutt'),
                        count: compactCounts.threePutt,
                        style: styles.compactScoreHazardBadgeThreeputt,
                        textStyle: styles.compactScoreHazardBadgeTextDark,
                      },
                      {
                        key: 'penalty',
                        label: t('hole.event.penalty'),
                        count: compactCounts.penalty,
                        style: styles.compactScoreHazardBadgeOther,
                        textStyle: styles.compactScoreHazardBadgeTextDark,
                      },
                    ].filter((entry) => entry.count > 0);
                    return (
                      <View style={styles.compactScoreActions}>
                        {mysteryWinnerIdsForHole.has(player.id) ? (
                          <Image source={MysteryPrizeIcon} style={styles.compactScoreMysteryIcon} resizeMode="contain" />
                        ) : null}
                        <View
                          {...compactRowScoreSwipeResponder.panHandlers}
                          onTouchStart={() => setIsScoreSwipeActive(true)}
                          onTouchEnd={() => setIsScoreSwipeActive(false)}
                          onTouchCancel={() => setIsScoreSwipeActive(false)}
                        >
                          <Pressable
                            ref={playerIndex === 0 ? compactScoreButtonCoachMarkRef : undefined}
                            style={styles.compactScoreButton}
                            onPress={() => setScorePopupPlayerId(player.id)}
                          >
                            <Image
                              source={ScorePopupLargeButtonImage}
                              style={styles.compactScoreButtonImage}
                              resizeMode="contain"
                              accessible={false}
                            />
                            <Text
                              style={[
                                styles.compactScoreButtonText,
                                !displayScore && styles.compactScoreButtonTextPlaceholder,
                              ]}
                            >
                              {displayScore ?? t('hole.scoreButtonLabel')}
                            </Text>
                            {compactHazardBadges.length > 0 ? (
                              <View style={styles.compactScoreHazardBadges}>
                                {compactHazardBadges.map((badge) => (
                                  <View key={`${player.id}-${badge.key}`} style={[styles.compactScoreHazardBadge, badge.style]}>
                                    <Text style={[styles.compactScoreHazardBadgeTextBase, badge.textStyle]}>
                                      {badge.label.charAt(0)}
                                    </Text>
                                  </View>
                                ))}
                              </View>
                            ) : null}
                          </Pressable>
                        </View>
                        {isStablefordGame ? (
                          <Text style={styles.compactStablefordText}>
                            {stablefordPoints !== null ? String(stablefordPoints) : '–'}
                          </Text>
                        ) : null}
                      </View>
                    );
                  })()}
                </View>
                );
                })()
              ))}
            </View>
            <Modal
              visible={!!scorePopupPlayerId}
              transparent
              animationType="none"
              onRequestClose={closeScorePopup}
            >
              <Pressable
                style={[
                  styles.modalBackdrop,
                  styles.scoreModalBackdrop,
                  shouldTopAlignScorePopup && styles.scoreModalBackdropTop,
                ]}
                onPress={closeScorePopup}
              >
                <Pressable onPress={() => null}>
                  <Animated.View
                    style={[
                      styles.modalCard,
                      styles.scoreModalCard,
                      shouldTopAlignScorePopup ? { marginBottom: 0, maxHeight: windowHeight - 24 } : null,
                      { transform: [{ translateY: scorePopupSheetTranslateY }] },
                    ]}
                  >
                  <ScrollView
                    style={styles.scorePopupScroll}
                    contentContainerStyle={styles.scorePopupScrollContent}
                    showsVerticalScrollIndicator
                    bounces={false}
                  >
                    {(() => {
                      const name = scorePopupPlayer?.name ?? '';
                      const hcapStrokes =
                        scorePopupPlayer && strokeIndexForHole !== null
                          ? ((hasExactHoleData ? holeEntries.find((h) => h.playerId === scorePopupPlayer.id) : undefined)?.handicapStrokes ??
                              computeStrokesReceivedForHole(scorePopupPlayer.handicap ?? null, strokeIndexForHole)) ?? 0
                          : 0;
                      return (
                        <>
                          <Text style={[styles.modalTitle, styles.scorePopupTitle]}>
                            {t('hole.scorePopupTitle', {
                              name,
                              holeLabel: t('hole.title'),
                              hole: holeNumber,
                            })}
                          </Text>
                          {scorePopupPlayer ? (
                            <Text style={styles.scorePopupSubtitle}>
                              {t('hole.scorePopupHandicapAndIndexLabel', {
                                handicap: `+${hcapStrokes}`,
                                index: strokeIndexForHole ?? '-',
                              })}
                            </Text>
                          ) : null}
                        </>
                      );
                    })()}
                    <View style={styles.scorePopupGrid}>
                    {scorePopupPlayer ? (
                      <View style={styles.scorePopupHazardColumn}>
                        {showWaterEvent ? (
                          <View style={styles.scorePopupHazardButton}>
                            <Text style={styles.scorePopupHazardLabel}>{t('hole.event.water')}</Text>
                            <View style={styles.scorePopupHazardControlRow}>
                              <Pressable
                                style={styles.scorePopupHazardAdjustButton}
                                onPress={() => updateCompactHazardCount(scorePopupPlayer, 'water', -1)}
                                accessibilityRole="button"
                                accessibilityLabel={`${t('hole.event.water')} -`}
                              >
                                <Text style={styles.scorePopupHazardAdjustText}>-</Text>
                              </Pressable>
                              <Pressable
                                style={styles.scorePopupHazardIconWrap}
                                onPress={() => updateCompactHazardCount(scorePopupPlayer, 'water', 1)}
                                accessibilityRole="button"
                                accessibilityLabel={t('hole.event.water')}
                              >
                                <Image source={WaterIcon} style={styles.scorePopupHazardIcon} resizeMode="contain" />
                                <Text style={styles.scorePopupHazardCount}>{scorePopupEventCounts.water}</Text>
                              </Pressable>
                              <Pressable
                                style={styles.scorePopupHazardAdjustButton}
                                onPress={() => updateCompactHazardCount(scorePopupPlayer, 'water', 1)}
                                accessibilityRole="button"
                                accessibilityLabel={`${t('hole.event.water')} +`}
                              >
                                <Text style={styles.scorePopupHazardAdjustText}>+</Text>
                              </Pressable>
                            </View>
                          </View>
                        ) : null}
                        {showPenaltyEvent ? (
                          <View style={styles.scorePopupHazardButton}>
                            <Text style={styles.scorePopupHazardLabel}>{t('hole.event.penalty')}</Text>
                            <View style={styles.scorePopupHazardControlRow}>
                              <Pressable
                                style={styles.scorePopupHazardAdjustButton}
                                onPress={() => updateCompactHazardCount(scorePopupPlayer, 'penalty', -1)}
                                accessibilityRole="button"
                                accessibilityLabel={`${t('hole.event.penalty')} -`}
                              >
                                <Text style={styles.scorePopupHazardAdjustText}>-</Text>
                              </Pressable>
                              <Pressable
                                style={styles.scorePopupHazardIconWrap}
                                onPress={() => updateCompactHazardCount(scorePopupPlayer, 'penalty', 1)}
                                accessibilityRole="button"
                                accessibilityLabel={t('hole.event.penalty')}
                              >
                                <Image source={PenaltyIcon} style={styles.scorePopupHazardIcon} resizeMode="contain" />
                                <Text style={styles.scorePopupHazardCount}>{scorePopupEventCounts.penalty}</Text>
                              </Pressable>
                              <Pressable
                                style={styles.scorePopupHazardAdjustButton}
                                onPress={() => updateCompactHazardCount(scorePopupPlayer, 'penalty', 1)}
                                accessibilityRole="button"
                                accessibilityLabel={`${t('hole.event.penalty')} +`}
                              >
                                <Text style={styles.scorePopupHazardAdjustText}>+</Text>
                              </Pressable>
                            </View>
                          </View>
                        ) : null}
                        {showBunkerEvent ? (
                          <View style={styles.scorePopupHazardButton}>
                            <Text style={styles.scorePopupHazardLabel}>{t('hole.event.bunker')}</Text>
                            <View style={styles.scorePopupHazardControlRow}>
                              <Pressable
                                style={styles.scorePopupHazardAdjustButton}
                                onPress={() => updateCompactHazardCount(scorePopupPlayer, 'bunker', -1)}
                                accessibilityRole="button"
                                accessibilityLabel={`${t('hole.event.bunker')} -`}
                              >
                                <Text style={styles.scorePopupHazardAdjustText}>-</Text>
                              </Pressable>
                              <Pressable
                                style={styles.scorePopupHazardIconWrap}
                                onPress={() => updateCompactHazardCount(scorePopupPlayer, 'bunker', 1)}
                                accessibilityRole="button"
                                accessibilityLabel={t('hole.event.bunker')}
                              >
                                <Image source={BunkerIcon} style={styles.scorePopupHazardIcon} resizeMode="contain" />
                                <Text style={styles.scorePopupHazardCount}>{scorePopupEventCounts.bunker}</Text>
                              </Pressable>
                              <Pressable
                                style={styles.scorePopupHazardAdjustButton}
                                onPress={() => updateCompactHazardCount(scorePopupPlayer, 'bunker', 1)}
                                accessibilityRole="button"
                                accessibilityLabel={`${t('hole.event.bunker')} +`}
                              >
                                <Text style={styles.scorePopupHazardAdjustText}>+</Text>
                              </Pressable>
                            </View>
                          </View>
                        ) : null}
                        {showThreePuttEvent ? (
                          <View style={styles.scorePopupHazardButton}>
                            <Text style={styles.scorePopupHazardLabel}>{t('hole.event.threePutt')}</Text>
                            <View style={styles.scorePopupHazardControlRow}>
                              <Pressable
                                style={styles.scorePopupHazardAdjustButton}
                                onPress={() => updateCompactHazardCount(scorePopupPlayer, 'threePutt', -1)}
                                accessibilityRole="button"
                                accessibilityLabel={`${t('hole.event.threePutt')} -`}
                              >
                                <Text style={styles.scorePopupHazardAdjustText}>-</Text>
                              </Pressable>
                              <Pressable
                                style={styles.scorePopupHazardIconWrap}
                                onPress={() => updateCompactHazardCount(scorePopupPlayer, 'threePutt', 1)}
                                accessibilityRole="button"
                                accessibilityLabel={t('hole.event.threePutt')}
                              >
                                <Image source={ThreePuttIcon} style={styles.scorePopupHazardIcon} resizeMode="contain" />
                                <Text style={styles.scorePopupHazardCount}>{scorePopupEventCounts.threePutt}</Text>
                              </Pressable>
                              <Pressable
                                style={styles.scorePopupHazardAdjustButton}
                                onPress={() => updateCompactHazardCount(scorePopupPlayer, 'threePutt', 1)}
                                accessibilityRole="button"
                                accessibilityLabel={`${t('hole.event.threePutt')} +`}
                              >
                                <Text style={styles.scorePopupHazardAdjustText}>+</Text>
                              </Pressable>
                            </View>
                          </View>
                        ) : null}
                        {mysteryEntriesForHole.map((entry) => {
                          const selected = mysteryWinnersBySubgame[entry.subgameId]?.playerId === scorePopupPlayer.id;
                          return (
                            <Pressable
                              key={`score-popup-mystery-hazard-${entry.subgameId}`}
                              style={[
                                styles.scorePopupMysteryButton,
                                selected && styles.scorePopupMysteryButtonSelected,
                              ]}
                              onPress={() => handleMysteryWinnerSelect(entry.subgameId, scorePopupPlayer.id)}
                              accessibilityRole="button"
                              accessibilityState={{ selected }}
                              accessibilityLabel={subgameNameById[entry.subgameId] ?? t('hole.mysteryPrizeUnknownSubgame')}
                            >
                              <Image source={MysteryPrizeIcon} style={styles.scorePopupMysteryIcon} resizeMode="contain" />
                              <Text style={styles.scorePopupMysteryText} numberOfLines={2}>
                                {subgameNameById[entry.subgameId] ?? t('hole.mysteryPrizeUnknownSubgame')}
                              </Text>
                            </Pressable>
                          );
                        })}
                      </View>
                    ) : null}
                    <View style={styles.scorePopupLeftColumn} pointerEvents="box-none">
                          <Image source={ScorePopupLongButtonImage} style={styles.scorePopupTopPlateImage} resizeMode="contain" pointerEvents="none" />
                          <Text style={styles.scorePopupTopPlateText} numberOfLines={1} pointerEvents="none">{`Par ${parLabel}`}</Text>
                          <Image source={ScorePopupRailBannerImage} style={styles.scorePopupRailImage} resizeMode="stretch" pointerEvents="none" />
                          <NativeView style={styles.scorePopupRailMarksColumn}>
                            {Array.from({ length: 8 }).map((_, index) => (
                              <Pressable
                                key={`score-popup-rail-mark-${index}`}
                                style={[
                                  styles.scorePopupRailMarkSlot,
                                  index === 0 && styles.scorePopupRailMarkIconFirst,
                                ]}
                                onPress={() => {
                                  if (!scorePopupPlayer) return;
                                  if (index === 7) {
                                    setScorePopupEightPlusOpen(true);
                                    return;
                                  }
                                  handleScore(scorePopupPlayer, String(index + 1));
                                  closeScorePopup();
                                }}
                                hitSlop={8}
                                accessibilityRole="button"
                                accessibilityLabel={
                                  index === 7
                                    ? t('hole.scoreButtonLabel', { value: '8+' })
                                    : t('hole.scoreButtonLabel', { value: String(index + 1) })
                                }
                              >
                                <Image
                                  source={HoleMarkImage}
                                  style={styles.scorePopupRailMarkIcon}
                                  resizeMode="contain"
                                  accessible={false}
                                />
                                {scorePopupParMarkIndex === index ? <NativeView style={styles.scorePopupRailParRing} /> : null}
                                {scorePopupSelectedMarkIndex === index ? (
                                  <Image source={PegImage} style={styles.scorePopupRailPegIcon} resizeMode="contain" accessible={false} />
                                ) : null}
                              </Pressable>
                            ))}
                          </NativeView>
                    </View>
                    <View style={styles.scorePopupRows}>
                    {(() => {
                      const selected = scorePopupPlayer
                        ? (hasExactHoleData ? (scores[scorePopupPlayer.id] ?? scores[scorePopupPlayer.name]) : null)
                        : null;
                      return (
                        <>
                    {[...Array(7)].map((_, idx) => String(idx + 1)).concat('8+').map((val) => {
                      const isParScore = parForHole ? val === String(parForHole) : false;
                      const selectedNumeric = selected && selected !== 'Pickup' ? Number.parseInt(selected, 10) : Number.NaN;
                      const selectedIsEightPlus =
                        val === '8+' && (selected === 'Pickup' || (Number.isFinite(selectedNumeric) && selectedNumeric >= 8));
                      const displayValue =
                        val === '8+'
                          ? selected === 'Pickup'
                            ? t('hole.pickup')
                            : selectedIsEightPlus
                              ? t('hole.eightPlusLabelWithValue', { value: selectedNumeric })
                              : val
                          : val;
                      return (
                        <Pressable
                          key={val}
                          style={[
                            styles.scorePopupButton,
                            isParScore && styles.scorePopupButtonPar,
                            (selected === val || selectedIsEightPlus) && styles.scorePopupButtonSelected,
                          ]}
                          onPress={() => {
                            if (!scorePopupPlayer) return;
                            if (val === '8+') {
                              setScorePopupEightPlusOpen(true);
                              return;
                            }
                            handleScore(scorePopupPlayer, val);
                            closeScorePopup();
                          }}
                          accessibilityRole="button"
                          accessibilityState={{ selected: selected === val || selectedIsEightPlus }}
                        >
                          <Image
                            source={ScorePopupLargeButtonImage}
                            style={styles.scorePopupButtonImage}
                            resizeMode="stretch"
                            accessible={false}
                          />
                          <Text
                            style={[
                              styles.scorePopupButtonText,
                              isParScore && styles.scorePopupButtonTextPar,
                            ]}
                          >
                            {displayValue}
                          </Text>
                        </Pressable>
                      );
                    })}
                        </>
                      );
                    })()}
                    </View>
                    </View>
                  {scorePopupEightPlusOpen && scorePopupPlayer ? (
                    <View style={styles.scorePopupEightPlusOverlay}>
                      <Pressable style={styles.scorePopupEightPlusBackdrop} onPress={() => setScorePopupEightPlusOpen(false)} />
                      <View style={styles.scorePopupEightPlusCard}>
                        {['8', '9', '10', '11', '12', 'Pickup'].map((option) => (
                          <Pressable
                            key={`eight-plus-option-${option}`}
                            style={styles.scorePopupEightPlusOption}
                            onPress={() => {
                              handleScore(scorePopupPlayer, option);
                              setScorePopupEightPlusOpen(false);
                              closeScorePopup();
                            }}
                          >
                            <Text style={styles.scorePopupEightPlusOptionText}>{option === 'Pickup' ? t('hole.pickup') : option}</Text>
                          </Pressable>
                        ))}
                      </View>
                    </View>
                  ) : null}
                  </ScrollView>
                  <GoffyButton
                    label="X"
                    onPress={closeScorePopup}
                    tone="dark"
                    oldButtonStyle={styles.modalCloseButtonBlue}
                    style={styles.scorePopupCloseButton}
                    textStyle={styles.scorePopupCloseButtonText}
                    accessibilityLabel={t('hole.close')}
                  />
                  </Animated.View>
                </Pressable>
              </Pressable>
            </Modal>
            <Modal
              visible={!!selectedPlayerId}
              transparent
              animationType="fade"
              onRequestClose={() => setSelectedPlayerId(null)}
            >
              <Pressable style={styles.modalBackdrop} onPress={() => setSelectedPlayerId(null)}>
                <Pressable style={styles.modalCard} onPress={() => null}>
                  <Text style={styles.modalTitle}>
                    {t('hole.playerBreakdownTitle', { name: selectedPlayer?.name ?? '' })}
                  </Text>
                  {selectedPlayerGamePoints.length > 0 ? (
                    selectedPlayerGamePoints.map((row) => (
                      <View key={row.id} style={styles.modalRow}>
                        <View style={styles.modalLabelGroup}>
                          <Image source={row.icon} style={styles.modalGameIcon} resizeMode="contain" />
                          <Text style={styles.modalLabel}>
                            {row.name}
                            {(() => {
                              const pos = row.position ?? (selectedPlayer?.id ? gamePositionsByPlayer[row.id]?.[selectedPlayer.id] : null);
                              if (!pos) return '';
                              const isShared = !!positionShareByGame[row.id]?.[selectedPlayer?.id ?? ''];
                              const shareLabel = isShared ? `, ${t('hole.positionShared')}` : '';
                              return ` (${t('hole.positionShort', { position: pos })}${shareLabel})`;
                            })()}
                          </Text>
                        </View>
                        <Text style={styles.modalValue}>{row.points}</Text>
                      </View>
                    ))
                  ) : (
                    <Text style={styles.modalEmpty}>{t('hole.playerBreakdownEmpty')}</Text>
                  )}
                  <GoffyButton
                    label={t('hole.close')}
                    onPress={() => setSelectedPlayerId(null)}
                    tone="dark"
                    oldButtonStyle={styles.modalCloseButtonBlue}
                    style={[styles.modalCloseButton, styles.playerBreakdownOkButton]}
                    textStyle={styles.modalCloseText}
                    accessibilityLabel={t('hole.close')}
                  />
                </Pressable>
              </Pressable>
            </Modal>
            <Modal
              visible={nextMysteryModalOpen}
              transparent
              animationType="fade"
              onRequestClose={() => setNextMysteryModalOpen(false)}
            >
              <Pressable style={styles.modalBackdrop} onPress={() => setNextMysteryModalOpen(false)}>
                <Pressable style={styles.modalCard} onPress={() => null}>
                  <Text style={styles.modalTitle}>{t('hole.mysteryPrizeNextHoleTitle')}</Text>
                  <Text style={styles.modalSubtitle}>{t('hole.mysteryPrizeNextHoleSubtitle', { hole: holeNumber + 1 })}</Text>
                  {nextMysteryModalRows.map((row, index) => (
                    <View key={`${row.subgame}:${row.player}:${index}`} style={styles.modalRow}>
                      <Text style={styles.modalLabel}>{row.subgame}</Text>
                      <Text style={styles.modalValue}>{row.player}</Text>
                    </View>
                  ))}
                  <GoffyButton
                    label={t('hole.backButton')}
                    onPress={() => setNextMysteryModalOpen(false)}
                    tone="dark"
                    oldButtonStyle={styles.modalCloseButtonBlue}
                    style={styles.modalBackButton}
                    textStyle={styles.modalBackText}
                    accessibilityLabel={t('hole.close')}
                  />
                </Pressable>
              </Pressable>
            </Modal>
            {holeMenuPopup}
            <View style={styles.navRowBottom}>
              <GoffyButton
                label={t('hole.prevHole')}
                onPress={() =>
                  holeNumber > 1 && viewModel.actions.navigateToHole(holeNumber - 1)
                }
                disabled={holeNumber <= 1}
                tone="dark"
                style={styles.compactNavButtonBlue}
                textStyle={styles.compactNavBlueButtonText}
              />
              <GoffyButton
                label={holeNumber >= holesCount ? t('hole.finishRound') : t('hole.nextHole')}
                onPress={() => {
                  if (holeNumber >= holesCount) {
                    viewModel.actions.finishRound();
                    return;
                  }
                  viewModel.actions.navigateToHole(holeNumber + 1);
                }}
                tone="accent"
                style={[styles.compactNavButtonBlue, styles.compactNavButtonPrimary]}
                textStyle={[styles.compactNavBlueButtonText, styles.compactNavButtonPrimaryText]}
              />
            </View>
          {gameResultsPanel ? (
              <View style={styles.compactGameResultsUnderNav}>
                {gameResultsPanel}
              </View>
            ) : null}
          </View>
          </View>
      </ScrollView>
      <CoachMarkCallout
        visible={showCompactScoreCoachMark}
        targetRef={compactScoreButtonCoachMarkRef}
        title={t('coachMarks.holeScoreTitle')}
        description={t('coachMarks.holeScoreBody')}
        actionLabel={t('coachMarks.gotIt')}
        onDismiss={() => setCompactScoreCoachMarkDismissed(true)}
      />
      {birdsVisible ? (
        <View style={[styles.birdieOverlay, { pointerEvents: 'none' }]}>
          <Animated.Image
            source={BirdieImage}
            style={[
              styles.birdieImage,
              {
                opacity: birdieOpacity,
                transform: [
                  { scale: birdieScale },
                  {
                    rotate: birdieRotate.interpolate({
                      inputRange: [0, 1],
                      outputRange: ['0deg', '360deg'],
                    }),
                  },
                ],
              },
            ]}
            resizeMode="contain"
          />
        </View>
      ) : null}
      </View>
    );
}

function generateTraceId() {
  const uuid = (crypto as { randomUUID?: () => string }).randomUUID?.();
  if (uuid) return uuid;
  return `trace-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
